// src/pages/client/StoreRequest/RequestStore.jsx

import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { packageApi, onboardingApi } from "../../../api/packageApi";
import { storeRequestApi } from "../../../api/storeRequestApi";
import { paymentApi } from "../../../api/paymentApi";
import { domainApi } from "../../../api/domainApi";
import { useAuth } from "../../../context/AuthContext";
import useOnboardingDraft from "../../../hooks/useOnboardingDraft";
import BusinessProfileStep from "./BusinessProfileStep";
import Privacy from "../../website/Privacy";
import { DEFAULT_CONTRACT_TEMPLATES } from "./contractTemplates";
import DomainPriceNoticeModal from "./DomainPriceNoticeModal";
import "./RequestStore.css";

const EFT_DETAIL_LABELS = {
  bankName: "Bank",
  accountHolder: "Account holder",
  accountNumber: "Account number",
  branchCode: "Branch code",
  accountType: "Account type",
  paymentReference: "Payment reference",
};

const DOMAIN_TAX_RATE = 0.15;
const INCLUDED_DOMAIN_EXTENSION = ".co.za";
const DEFAULT_DOMAIN_EXTENSIONS = [
  { extension: ".co.za", price: 89, currency: "ZAR", popular: true },
  { extension: ".com", price: 129.99, currency: "ZAR", popular: true },
  { extension: ".org", price: 119.99, currency: "ZAR", popular: false },
  { extension: ".net", price: 119.99, currency: "ZAR", popular: false },
  { extension: ".shop", price: 99.99, currency: "ZAR", popular: false },
  { extension: ".store", price: 99.99, currency: "ZAR", popular: false },
  { extension: ".online", price: 89.99, currency: "ZAR", popular: false },
];
const roundMoney = (amount) =>
  Math.round((amount + Number.EPSILON) * 100) / 100;
const createSubdomainFromBusinessName = (businessName) => {
  const slug = String(businessName || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 48)
    .replace(/-+$/g, "");
  return slug.length >= 3 ? slug : `${slug || "my"}-store`;
};
const isValidExistingDomain = (domain) =>
  /^(?=.{1,253}$)(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/i.test(
    String(domain || "").trim(),
  );
const parseRequestRequirements = (value) => {
  if (!value) return {};
  if (typeof value === "object") return value;
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

const EFTDetailList = ({ details, instructions, fallback }) => {
  const configuredFields = Object.entries(EFT_DETAIL_LABELS).filter(([field]) =>
    String(details?.[field] || "").trim(),
  );

  if (configuredFields.length) {
    return (
      <dl className="bank-details-fields">
        {configuredFields.map(([field, label]) => (
          <div key={field}>
            <dt>{label}</dt>
            <dd>{details[field]}</dd>
          </div>
        ))}
      </dl>
    );
  }

  return (
    <div className="bank-details-instructions">{instructions || fallback}</div>
  );
};

const RequestStore = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const editRequestId = new URLSearchParams(location.search).get("requestId");

  // ============ STATE ============
  const [packages, setPackages] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [currentStep, setCurrentStep] = useState(0);
  const [domainPriceNoticeAcknowledged, setDomainPriceNoticeAcknowledged] =
    useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [direction, setDirection] = useState(1);
  const [showFloatingButtons, setShowFloatingButtons] = useState(false);
  const [isGuideOpen, setIsGuideOpen] = useState(false);
  const [guideStepIndex, setGuideStepIndex] = useState(0);
  const [guideTargetRect, setGuideTargetRect] = useState(null);
  const [agreementAccepted, setAgreementAccepted] = useState(false);
  const [agreementError, setAgreementError] = useState("");
  const [legalFirstName, setLegalFirstName] = useState("");
  const [legalSurname, setLegalSurname] = useState("");
  const [legalModal, setLegalModal] = useState("");
  const [contractTemplates, setContractTemplates] = useState(
    DEFAULT_CONTRACT_TEMPLATES,
  );
  const [brandLogo, setBrandLogo] = useState("");
  const [assessmentAnswers, setAssessmentAnswers] = useState({});
  const [draftTouched, setDraftTouched] = useState(false);
  const [draftCompleted, setDraftCompleted] = useState(false);
  const [assessmentComplete, setAssessmentComplete] = useState(false);
  const [isTrial, setIsTrial] = useState(Boolean(location.state?.trial));
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState("");
  const [submittedRequestId, setSubmittedRequestId] = useState("");
  const [editRequestReady, setEditRequestReady] = useState(!editRequestId);
  const [editRequestError, setEditRequestError] = useState("");
  const [paymentResult, setPaymentResult] = useState(null);
  const [gatewayCheckout, setGatewayCheckout] = useState(null);
  const [redirectingToGateway, setRedirectingToGateway] = useState(false);
  const gatewayFormRef = useRef(null);
  const [invoiceNumber, setInvoiceNumber] = useState(() => {
    const date = new Date();
    return `INV-TF-${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}${String(date.getDate()).padStart(2, "0")}-${String(Math.floor(Math.random() * 10000)).padStart(4, "0")}`;
  });
  const [agreementNumber, setAgreementNumber] = useState(() => {
    const date = new Date();
    return `TF-${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}${String(date.getDate()).padStart(2, "0")}-${String(Math.floor(Math.random() * 1000)).padStart(3, "0")}`;
  });

  useEffect(() => {
    if (!gatewayCheckout || !gatewayFormRef.current) return;
    gatewayFormRef.current.requestSubmit();
  }, [gatewayCheckout]);

  useEffect(() => {
    const handlePageShow = (event) => {
      if (!event.persisted) return;
      setSubmitting(false);
      setRedirectingToGateway(false);
      setGatewayCheckout(null);
      setPaymentResult(null);
    };
    window.addEventListener("pageshow", handlePageShow);
    return () => window.removeEventListener("pageshow", handlePageShow);
  }, []);

  // Dynamic steps from admin configuration
  const [dynamicSteps, setDynamicSteps] = useState([]);
  const [stepsConfigured, setStepsConfigured] = useState(false);
  const [stepsLoading, setStepsLoading] = useState(true);

  // Domain Settings from admin
  const [domainSettings, setDomainSettings] = useState({
    enable_availability_check: true,
    enable_dns_config: false,
    domain_api_provider: "godaddy",
    auto_configure_dns: false,
  });

  // STEP 2: Business Details
  const [formData, setFormData] = useState({
    businessName: "",
    businessType: "",
    contactName: "",
    email: user?.Email || "",
    phone: "",
    website: "",
    description: "",
  });

  // STEP 3: Domain Setup
  const [domainData, setDomainData] = useState({
    domainType: isTrial ? "subdomain" : "custom",
    subdomain: "",
    existingDomain: "",
    customDomain: "",
    selectedExtension: ".co.za",
    availability: null,
    suggestions: [],
    verificationStatus: null,
    domainPrice: null,
    domainCurrency: "ZAR",
    isFree: false,
    yearsToRegister: 1,
  });
  const [domainExtensions, setDomainExtensions] = useState(
    DEFAULT_DOMAIN_EXTENSIONS,
  );
  const [isChecking, setIsChecking] = useState(false);
  const [domainError, setDomainError] = useState("");
  const [domainLoading, setDomainLoading] = useState(false);
  const [showDomainCostNotice, setShowDomainCostNotice] = useState(false);
  const [domainAvailabilityAcknowledged, setDomainAvailabilityAcknowledged] =
    useState(false);

  // STEP 4: Store Design
  const [designData, setDesignData] = useState({
    template: "minimal",
    primaryColor: "#1a237e",
    secondaryColor: "#00c99c",
    font: "Poppins",
    logo: null,
    banner: null,
  });

  // STEP 5: Products
  const [products, setProducts] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [currentProduct, setCurrentProduct] = useState({
    name: "",
    price: "",
    description: "",
    stock: "",
    category: "",
    image: null,
  });
  const [productErrors, setProductErrors] = useState({});
  const restoreDraft = useCallback(
    (draft) => {
      setSelectedPackage(draft.selectedPackage || null);
      setIsTrial(Boolean(draft.isTrial));
      setCurrentStep(
        Number.isInteger(draft.currentStep) ? draft.currentStep : 0,
      );
      setFormData((current) => ({ ...current, ...draft.formData }));
      setDomainData((current) => ({
        ...current,
        ...draft.domainData,
        availability: null,
        verificationStatus: null,
      }));
      setDesignData((current) => ({
        ...current,
        ...draft.designData,
        logo: null,
        banner: null,
      }));
      setProducts(
        Array.isArray(draft.products)
          ? draft.products.map((product) => ({ ...product, image: null }))
          : [],
      );
      setAssessmentAnswers(draft.assessmentAnswers || {});
      setLegalFirstName(draft.legalFirstName || "");
      setLegalSurname(draft.legalSurname || "");
      setSelectedPaymentMethod(draft.selectedPaymentMethod || "");
      setSubmittedRequestId(draft.submittedRequestId || "");
      setInvoiceNumber(draft.invoiceNumber || invoiceNumber);
      setAgreementNumber(draft.agreementNumber || agreementNumber);
      setAgreementAccepted(false);
      setDomainAvailabilityAcknowledged(false);
      setDraftTouched(true);
    },
    [agreementNumber, invoiceNumber],
  );
  const restoreDraftRef = useRef(restoreDraft);
  restoreDraftRef.current = restoreDraft;

  useEffect(() => {
    if (!editRequestId) {
      setEditRequestReady(true);
      return undefined;
    }
    if (loading || stepsLoading) return undefined;
    let active = true;

    const loadRequestForEditing = async () => {
      try {
        const response = await storeRequestApi.getRequestById(editRequestId);
        if (!active) return;
        const request = response.data;
        const lockedStatuses = new Set([
          "approved",
          "in_progress",
          "ready_for_review",
          "live",
          "suspended",
        ]);
        const canEdit =
          Boolean(request.ClientEditingUnlocked) ||
          (!request.ApprovedDate && !lockedStatuses.has(request.Status));
        if (!canEdit) {
          setEditRequestError(
            "This store request is locked. Ask an administrator to unlock it for editing.",
          );
          return;
        }

        const requirements = parseRequestRequirements(
          request.AdditionalRequirements,
        );
        const businessInfo = requirements.businessInfo || {};
        const domain =
          requirements.domain || {
            domainType: request.IsTrial
              ? "subdomain"
              : request.NeedsDomain
                ? "custom"
                : "existing",
            subdomain: request.PreferredStoreName || "",
            existingDomain: request.NeedsDomain
              ? ""
              : request.ExistingDomain || request.PreferredStoreName || "",
            customDomain: request.NeedsDomain
              ? String(request.ExistingDomain || "").replace(/\.[^.]+$/, "")
              : "",
            selectedExtension: request.NeedsDomain
              ? String(request.ExistingDomain || "").match(/\.[^.]+$/)?.[0] ||
                ".co.za"
              : ".co.za",
          };
        setSelectedPackage(request.PackageID);
        setIsTrial(Boolean(request.IsTrial));
        restoreDraftRef.current({
          selectedPackage: request.PackageID,
          isTrial: Boolean(request.IsTrial),
          currentStep: 1,
          formData: {
            ...businessInfo,
            businessName: businessInfo.businessName || request.BusinessName,
            businessType:
              businessInfo.businessType || request.BusinessCategory || "",
            email: businessInfo.email || request.Email || user?.Email || "",
            description:
              businessInfo.description || request.BusinessDescription || "",
          },
          domainData: domain,
          designData: requirements.design || {},
          products: Array.isArray(requirements.products)
            ? requirements.products
            : [],
          assessmentAnswers: requirements.onboardingAssessment || {},
          legalFirstName: businessInfo.legalFirstName || "",
          legalSurname: businessInfo.legalSurname || "",
          selectedPaymentMethod: requirements.invoice?.paymentMethod || "",
          submittedRequestId: request.StoreRequestID,
          invoiceNumber: requirements.invoice?.invoiceNumber || invoiceNumber,
          agreementNumber:
            requirements.contractAcceptance?.agreementNumber || agreementNumber,
        });
        setAssessmentComplete(true);
        setDraftTouched(true);
      } catch (requestError) {
        if (active) {
          setEditRequestError(
            requestError.response?.data?.error ||
              "Could not load this store request for editing.",
          );
        }
      } finally {
        if (active) setEditRequestReady(true);
      }
    };

    loadRequestForEditing();
    return () => {
      active = false;
    };
  }, [
    editRequestId,
    loading,
    stepsLoading,
    user?.Email,
  ]);

  const draftData = useMemo(() => {
    const filesToReattach = [];
    const logo = designData.logo;
    const banner = designData.banner;
    if (logo && typeof logo === "object" && logo.name)
      filesToReattach.push(logo.name);
    if (banner && typeof banner === "object" && banner.name)
      filesToReattach.push(banner.name);
    const savedProducts = products.map((product) => {
      const image = product.image;
      if (image && typeof image === "object" && image.name) {
        filesToReattach.push(image.name);
      }
      return {
        ...product,
        image: typeof image === "string" ? image : null,
      };
    });

    return {
      selectedPackage,
      isTrial,
      currentStep,
      formData,
      domainData: {
        ...domainData,
        availability: null,
        verificationStatus: null,
      },
      designData: {
        ...designData,
        logo: typeof logo === "string" ? logo : null,
        banner: typeof banner === "string" ? banner : null,
      },
      products: savedProducts,
      assessmentAnswers,
      legalFirstName,
      legalSurname,
      selectedPaymentMethod,
      submittedRequestId,
      invoiceNumber,
      agreementNumber,
      filesToReattach: [...new Set(filesToReattach)],
    };
  }, [
    agreementNumber,
    assessmentAnswers,
    currentStep,
    designData,
    domainData,
    formData,
    invoiceNumber,
    isTrial,
    legalFirstName,
    legalSurname,
    products,
    selectedPackage,
    selectedPaymentMethod,
    submittedRequestId,
  ]);

  const draftEnabled =
    String(user?.Role || "").toLowerCase() === "client" &&
    !user?.SupportSession &&
    (!editRequestId || !editRequestError);
  const draftApi = useMemo(
    () =>
      editRequestId
        ? {
            getDraft: () => storeRequestApi.getEditDraft(editRequestId),
            saveDraft: (data) =>
              storeRequestApi.saveEditDraft(editRequestId, data),
            deleteDraft: () =>
              storeRequestApi.deleteEditDraft(editRequestId),
          }
        : storeRequestApi,
    [editRequestId],
  );
  const {
    canSaveDraft,
    clearDraft,
    initialized: draftInitialized,
    saveDraft,
    status: draftStatus,
  } = useOnboardingDraft({
    api: draftApi,
    data: draftData,
    enabled: draftEnabled,
    autosaveEnabled: !draftCompleted && !submitting,
    hasContent:
      draftTouched &&
      Boolean(
        selectedPackage ||
        formData.businessName ||
        formData.phone ||
        formData.website ||
        formData.description ||
        Object.keys(assessmentAnswers).length ||
        products.length,
      ),
    onRestore: restoreDraft,
    ready:
      !loading &&
      !stepsLoading &&
      Boolean(user) &&
      (!editRequestId || editRequestReady),
    requestName: editRequestId ? "store request edit" : "store",
  });

  // Full label on desktop, short "Save" label on mobile (swapped via CSS)
  const renderSaveDraftButton = () =>
    draftEnabled ? (
      <button
        type="button"
        className="btn-secondary request-save-draft"
        data-tour="save-draft"
        onClick={saveDraft}
        disabled={!canSaveDraft || submitting}
        aria-label="Save as draft"
      >
        <i className="fas fa-floppy-disk" aria-hidden="true" />
        <span className="btn-label-full">Save as draft</span>
        <span className="btn-label-short">Save</span>
      </button>
    ) : null;

  const CUSTOM_DOMAIN_PRICE = 50;

  const templates = [
    {
      id: "minimal",
      name: "Minimal",
      description: "Clean & simple",
      icon: "🎯",
    },
    {
      id: "modern",
      name: "Modern",
      description: "Contemporary & sleek",
      icon: "✨",
    },
    {
      id: "classic",
      name: "Classic",
      description: "Timeless & elegant",
      icon: "🏛️",
    },
    {
      id: "vibrant",
      name: "Vibrant",
      description: "Bold & energetic",
      icon: "🌈",
    },
  ];

  const colors = [
    "#1a237e",
    "#2a5bd7",
    "#00c99c",
    "#ff6f00",
    "#c62828",
    "#4a148c",
    "#004d40",
  ];
  const fonts = ["Poppins", "Inter", "Roboto", "Lato", "Montserrat", "Raleway"];

  // ============ HELPER FUNCTIONS ============
  const getPackageById = (id) => {
    const packageId =
      typeof id === "object" ? id?.PackageID ?? id?.packageId ?? id?.id : id;
    return packages.find(
      (pkg) =>
        String(pkg.PackageID ?? pkg.packageId ?? pkg.id) ===
        String(packageId ?? ""),
    );
  };
  const selectedPackageDetails = getPackageById(selectedPackage);

  const isDomainFree = () => {
    return isTrial;
  };

  const getDomainBasePrice = (extension = domainData.selectedExtension) => {
    const configuredPrice = domainExtensions.find(
      (item) => item.extension === extension,
    )?.price;
    return Number(configuredPrice ?? CUSTOM_DOMAIN_PRICE);
  };

  const getIncludedDomainAllowance = () =>
    Number(
      domainExtensions.find(
        (item) => item.extension === INCLUDED_DOMAIN_EXTENSION,
      )?.price ?? DEFAULT_DOMAIN_EXTENSIONS[0].price,
    );

  const isLitePackage = () =>
    String(selectedPackageDetails?.Tier || "")
      .trim()
      .toLowerCase() === "lite";

  const getDomainChargeBeforeTax = (
    extension = domainData.selectedExtension,
  ) => {
    if (isTrial || domainData.domainType !== "custom") return 0;
    const selectedPrice = getDomainBasePrice(extension);
    return isLitePackage()
      ? selectedPrice
      : Math.max(0, selectedPrice - getIncludedDomainAllowance());
  };

  const getDomainTax = (basePrice) => roundMoney(basePrice * DOMAIN_TAX_RATE);

  const getDomainPriceWithTax = (basePrice) =>
    roundMoney(basePrice + getDomainTax(basePrice));

  const getCurrentPaymentDue = () => {
    const packageCharge = isTrial
      ? 0
      : Number(selectedPackageDetails?.Price || 0);
    return roundMoney(
      packageCharge + getDomainPriceWithTax(getDomainChargeBeforeTax()),
    );
  };

  // ============================================================
  // DYNAMIC STEP HELPERS
  // ============================================================

  // Get enabled steps (IsEnabled = true)
  const getEnabledSteps = () => {
    if (stepsConfigured) {
      const selectedTier = String(
        getPackageById(selectedPackage)?.Tier || "",
      ).toLowerCase();
      const businessStep = dynamicSteps.find(
        (step) => step.id === "business",
      ) || {
        id: "business",
        name: "Business",
        title: "Tell Us About Your Business",
        description: "Provide the details we need to set up your store",
        icon: "🏪",
        color: "#7c3aed",
        order: 2,
      };
      const enabledSteps = dynamicSteps.filter((step) => {
        if (step.id === "business") return false;
        if (
          (step.enabled !== true && step.id !== "domain") ||
          step.id === "business-profile"
        ) {
          return false;
        }
        if (step.id === "domain") return true;
        if (!selectedTier || !Array.isArray(step.tiers) || !step.tiers.length) {
          return true;
        }
        return step.tiers.some(
          (tier) => String(tier).toLowerCase() === selectedTier,
        );
      });
      enabledSteps.push({
        ...businessStep,
        enabled: true,
        required: true,
        order: 2,
      });
      return enabledSteps
        .sort((a, b) => {
          const fixedOrder = { package: 1, business: 2, domain: 3 };
          const orderA =
            fixedOrder[a.id] || Number(a.order) + 3 || Number.MAX_SAFE_INTEGER;
          const orderB =
            fixedOrder[b.id] || Number(b.order) + 3 || Number.MAX_SAFE_INTEGER;
          return orderA - orderB;
        })
        .map((step, index) => ({ ...step, order: index + 1 }));
    }
    // Fallback to default 7 steps
    return [
      {
        id: "package",
        name: "Package",
        title: "Choose Your Package",
        description: "Select the plan that best fits your business needs",
        enabled: true,
        required: true,
        order: 1,
        icon: "📦",
        color: "#4f46e5",
      },
      {
        id: "business",
        name: "Business",
        title: "Tell Us About Your Business",
        description: "Provide the details we need to set up your store",
        enabled: true,
        required: true,
        order: 2,
        icon: "🏪",
        color: "#7c3aed",
      },
      {
        id: "domain",
        name: "Domain",
        title: "Domain Setup",
        description: "Choose your domain or subdomain for your store",
        enabled: true,
        required: true,
        order: 3,
        icon: "🌐",
        color: "#059669",
      },
      {
        id: "design",
        name: "Design",
        title: "Store Design",
        description: "Customize the look and feel of your store",
        enabled: true,
        required: false,
        order: 4,
        icon: "🎨",
        color: "#d97706",
      },
      {
        id: "products",
        name: "Products",
        title: "Products",
        description: "Add your products to start selling",
        enabled: true,
        required: false,
        order: 5,
        icon: "🏷️",
        color: "#dc2626",
      },
      {
        id: "agreement",
        name: "Agreement",
        title: "Service Agreement",
        description: "Review and accept the service agreement",
        enabled: true,
        required: true,
        order: 6,
        icon: "📄",
        color: "#8b5cf6",
      },
      {
        id: "invoice",
        name: "Invoice",
        title: "Invoice & Payment",
        description: "Review invoice and payment details",
        enabled: true,
        required: true,
        order: 7,
        icon: "🧾",
        color: "#0891b2",
      },
    ];
  };

  const getTotalSteps = () => {
    return getEnabledSteps().length;
  };

  const getStepByIndex = (index) => {
    const enabledSteps = getEnabledSteps();
    return enabledSteps[index - 1] || null;
  };

  const getStepIdByIndex = (index) => {
    const step = getStepByIndex(index);
    return step?.id || null;
  };

  // ============ EFFECTS ============
  useEffect(() => {
    if (location.state?.selectedPackage) {
      const packageSelection = location.state.selectedPackage;
      setSelectedPackage(
        typeof packageSelection === "object"
          ? packageSelection.PackageID ??
              packageSelection.packageId ??
              packageSelection.id ??
              null
          : packageSelection,
      );
    }
    setIsTrial(Boolean(location.state?.trial));
    fetchPackages();
    fetchDynamicSteps();
    fetchDomainSettings();
    fetchDomainExtensions();
  }, [location]);

  useEffect(() => {
    paymentApi
      .getMethods()
      .then((response) => {
        const candidates = response?.data?.data ?? response?.data ?? response;
        const receivedMethods = Array.isArray(candidates) ? candidates : [];
        const methods = receivedMethods.map((method) => ({
          ...method,
          code: String(method.code ?? method.Code ?? "")
            .trim()
            .toLowerCase(),
          displayName: method.displayName ?? method.DisplayName ?? "",
          online: Boolean(method.online ?? method.IsOnline),
          instructions: String(
            method.instructions ?? method.Instructions ?? "",
          ).trim(),
        }));
        const availableMethods = methods.filter((method) => method.code);
        setPaymentMethods(availableMethods);
        setSelectedPaymentMethod((current) =>
          availableMethods.some((method) => method.code === current)
            ? current
            : availableMethods.find((method) => method.code === "eft")?.code ||
              availableMethods[0]?.code ||
              "",
        );
      })
      .catch((paymentError) => {
        console.error("Error loading payment methods:", paymentError);
      });
  }, []);

  useEffect(() => {
    if (!legalModal) return undefined;
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setLegalModal("");
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [legalModal]);

  useEffect(() => {
    onboardingApi
      .getContractSettings()
      .then((response) => {
        const settings = response.data || {};
        if (settings.contractTemplates) {
          const templates =
            typeof settings.contractTemplates === "string"
              ? JSON.parse(settings.contractTemplates)
              : settings.contractTemplates;
          if (Array.isArray(templates) && templates.length > 0) {
            setContractTemplates(templates);
          }
        }
        setBrandLogo(settings.brandLogo || "");
      })
      .catch((contractError) => {
        console.error("Error loading contract branding:", contractError);
      });
  }, []);

  useEffect(() => {
    if (selectedPackageDetails) {
      if (submittedRequestId) return;
      setDomainData((prev) => ({
        ...prev,
        domainType: isTrial ? "subdomain" : "custom",
        isFree: isDomainFree(),
        availability: null,
        verificationStatus: null,
        existingDomain: "",
        customDomain: "",
      }));
    }
  }, [packages, selectedPackage, isTrial, submittedRequestId]);

  useEffect(() => {
    if (isTrial) {
      setDomainData((prev) => ({
        ...prev,
        domainType: "subdomain",
        subdomain: createSubdomainFromBusinessName(formData.businessName),
      }));
    }
  }, [formData.businessName, isTrial]);

  useEffect(() => {
    const handleScroll = () => {
      setShowFloatingButtons(window.scrollY > 300);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Debounced domain check - only if availability check is enabled
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      if (
        domainData.domainType === "custom" &&
        domainData.customDomain &&
        domainData.customDomain.length > 2 &&
        !isTrial &&
        domainSettings.enable_availability_check
      ) {
        checkGoDaddyAvailability(
          domainData.customDomain + domainData.selectedExtension,
        );
      }
    }, 800);

    return () => clearTimeout(delayDebounce);
  }, [
    domainData.customDomain,
    domainData.selectedExtension,
    domainData.domainType,
    domainSettings.enable_availability_check,
  ]);

  // Reset current step if it's out of bounds
  useEffect(() => {
    const total = getTotalSteps();
    if (total > 0 && currentStep > total) {
      setCurrentStep(1);
    }
  }, [dynamicSteps, currentStep]);

  // ============ API CALLS ============
  const fetchPackages = async () => {
    try {
      setLoading(true);
      const response = await packageApi.getPackages(true);

      let packagesData = [];
      if (response.success) {
        packagesData = response.data || [];
      } else if (Array.isArray(response)) {
        packagesData = response;
      } else {
        packagesData = [];
      }

      packagesData = packagesData.filter((pkg) => pkg.IsActive !== false);
      setPackages(packagesData);

      if (location.state?.selectedPackage) {
        const packageSelection = location.state.selectedPackage;
        const packageId =
          typeof packageSelection === "object"
            ? packageSelection.PackageID ??
              packageSelection.packageId ??
              packageSelection.id
            : packageSelection;
        const stillActive = packagesData.some(
          (pkg) =>
            String(pkg.PackageID ?? pkg.packageId ?? pkg.id) ===
            String(packageId ?? ""),
        );
        if (!stillActive) {
          setSelectedPackage(null);
          setError(
            "The selected package is no longer available. Please choose another.",
          );
        }
      }
    } catch (err) {
      setError("Failed to load packages. Please refresh and try again.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchDynamicSteps = async () => {
    try {
      setStepsLoading(true);
      const response = await onboardingApi.getAllSteps();

      if (response && response.success && Array.isArray(response.data)) {
        setDynamicSteps(
          response.data.map((step) => ({
            ...step,
            id: String(step.id ?? step.stepKey ?? "")
              .trim()
              .toLowerCase(),
          })),
        );
        setStepsConfigured(true);
        console.log("✅ Loaded onboarding steps:", response.data.length);
      }
    } catch (error) {
      console.error("Error fetching dynamic steps:", error);
    } finally {
      setStepsLoading(false);
    }
  };

  const fetchDomainSettings = async () => {
    try {
      const response = await onboardingApi.getDomainSettings();

      if (response && response.success && response.settings) {
        setDomainSettings({
          enable_availability_check:
            response.settings.enable_availability_check === "true",
          enable_dns_config: response.settings.enable_dns_config === "true",
          domain_api_provider:
            response.settings.domain_api_provider || "godaddy",
          auto_configure_dns: response.settings.auto_configure_dns === "true",
        });
        console.log("✅ Domain settings loaded:", response.settings);
      } else {
        console.warn("⚠️ No domain settings found, using defaults");
        setDomainSettings({
          enable_availability_check: true,
          enable_dns_config: false,
          domain_api_provider: "godaddy",
          auto_configure_dns: false,
        });
      }
    } catch (error) {
      console.error("Error fetching domain settings:", error);
      setDomainSettings({
        enable_availability_check: true,
        enable_dns_config: false,
        domain_api_provider: "godaddy",
        auto_configure_dns: false,
      });
    }
  };

  const fetchDomainExtensions = async () => {
    try {
      const response = await onboardingApi.getDomains();
      if (response?.success && Array.isArray(response.data)) {
        setDomainExtensions(response.data);
        setDomainData((previous) => ({
          ...previous,
          selectedExtension: response.data.some(
            (item) => item.extension === previous.selectedExtension,
          )
            ? previous.selectedExtension
            : response.data[0]?.extension || "",
        }));
      }
    } catch (error) {
      console.error("Error fetching domain prices:", error);
    }
  };

  const checkGoDaddyAvailability = async (fullDomain) => {
    if (!domainSettings.enable_availability_check) {
      return;
    }

    if (!fullDomain || fullDomain.length < 4) return;
    if (isDomainFree()) return;

    setIsChecking(true);
    setDomainError("");

    try {
      const result = await domainApi.checkAvailability(fullDomain);

      setDomainData((prev) => ({
        ...prev,
        availability: result,
        verificationStatus: result.available ? "available" : "unavailable",
        domainPrice: getDomainPriceWithTax(getDomainChargeBeforeTax()),
        suggestions: result.suggested || [],
      }));
    } catch (error) {
      console.error("Error checking domain:", error);
      setDomainData((prev) => ({
        ...prev,
        availability: {
          available: false,
          serviceOffline: true,
          domain: fullDomain,
          error: "Availability could not be checked. Please retry.",
        },
        verificationStatus: "failed",
        domainPrice: getDomainPriceWithTax(getDomainChargeBeforeTax()),
      }));
    } finally {
      setIsChecking(false);
    }
  };

  const handleVerifyDomain = async () => {
    if (!domainSettings.enable_availability_check) {
      setDomainError(
        "We cannot automatically verify domain availability at this stage. Please confirm that you understand your requested domain is subject to availability and registration approval.",
      );
      return;
    }

    if (!domainData.customDomain) {
      setDomainError("Please enter a domain");
      return;
    }

    setDomainLoading(true);
    setDomainError("");
    setDomainData((prev) => ({ ...prev, verificationStatus: "checking" }));

    try {
      const fullDomain = domainData.customDomain + domainData.selectedExtension;
      const availability = await domainApi.checkAvailability(fullDomain);

      if (availability.available) {
        setDomainData((prev) => ({
          ...prev,
          availability: availability,
          verificationStatus: "available",
          domainPrice: getDomainPriceWithTax(getDomainChargeBeforeTax()),
        }));
      } else {
        setDomainData((prev) => ({
          ...prev,
          availability: availability,
          verificationStatus: "unavailable",
          suggestions: availability.suggested || [],
        }));
        setDomainError("This domain is not available. Please try another.");
      }
    } catch (error) {
      setDomainData((prev) => ({ ...prev, verificationStatus: "failed" }));
      setDomainError("Failed to verify domain. Please try again.");
    } finally {
      setDomainLoading(false);
    }
  };

  const handleGetSuggestions = async () => {
    if (!domainData.customDomain || domainData.customDomain.length < 3) {
      setDomainError("Please enter at least 3 characters");
      return;
    }

    // Skip if availability check is disabled
    if (!domainSettings.enable_availability_check) {
      setDomainError(
        "Domain suggestions are not available when availability check is disabled.",
      );
      return;
    }

    try {
      const result = await domainApi.getDomainSuggestions(
        domainData.customDomain,
      );
      setDomainData((prev) => ({
        ...prev,
        suggestions: result.suggestions || [],
      }));
    } catch (error) {
      console.error("Error getting suggestions:", error);
    }
  };

  // ============ HANDLERS ============
  const handlePackageSelect = (packageId) => {
    setSelectedPackage(packageId);
    setError("");
    setValidationErrors({});
    setDomainData((prev) => ({
      ...prev,
      domainType: isTrial ? "subdomain" : "custom",
      isFree: isTrial,
      verificationStatus: null,
      availability: null,
      customDomain: "",
      existingDomain: "",
      subdomain: isTrial
        ? createSubdomainFromBusinessName(formData.businessName)
        : "",
    }));
    setDirection(1);
    setCurrentStep((step) => (step < getTotalSteps() ? step + 1 : step));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (validationErrors[name]) {
      setValidationErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  // ============ PRODUCT HANDLERS ============
  const handleProductChange = (e) => {
    const { name, value } = e.target;
    setCurrentProduct((prev) => ({ ...prev, [name]: value }));
    if (productErrors[name]) {
      setProductErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const addProduct = () => {
    const newErrors = {};
    if (!currentProduct.name.trim())
      newErrors.name = "Product name is required";
    if (!currentProduct.price || parseFloat(currentProduct.price) <= 0) {
      newErrors.price = "Valid price is required";
    }

    if (Object.keys(newErrors).length > 0) {
      setProductErrors(newErrors);
      return;
    }

    setProducts([...products, { ...currentProduct, id: Date.now() }]);
    setCurrentProduct({
      name: "",
      price: "",
      description: "",
      stock: "",
      category: "",
      image: null,
    });
    setShowAddForm(false);
    setProductErrors({});
  };

  const removeProduct = (id) => {
    setProducts(products.filter((p) => p.id !== id));
  };

  // ============ VALIDATION ============
  const validateStep1 = () => {
    const errors = {};
    if (!selectedPackage)
      errors.package = "Please select a package to continue.";
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateStep2 = () => {
    const errors = {};

    // Trim values before validation
    const businessName = formData.businessName?.trim() || "";
    const businessType = formData.businessType?.trim() || "";
    const contactName = formData.contactName?.trim() || "";
    const email = formData.email?.trim() || "";
    const phone = formData.phone?.trim() || "";

    if (!businessName) {
      errors.businessName = "Business name is required.";
    }
    if (!businessType) {
      errors.businessType = "Please select a business type.";
    }
    if (!contactName) {
      errors.contactName = "Contact name is required.";
    }
    if (!email) {
      errors.email = "Email address is required.";
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      errors.email = "Please enter a valid email address.";
    }
    if (!phone) {
      errors.phone = "Phone number is required.";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const isBusinessStepValid = () => {
    const businessName = formData.businessName?.trim() || "";
    const businessType = formData.businessType?.trim() || "";
    const contactName = formData.contactName?.trim() || "";
    const email = formData.email?.trim() || "";
    const phone = formData.phone?.trim() || "";

    return Boolean(
      businessName &&
        businessType &&
        contactName &&
        /\S+@\S+\.\S+/.test(email) &&
        phone,
    );
  };

  const validateDomain = () => {
    if (isTrial) {
      if (!domainData.subdomain || domainData.subdomain.length < 3) {
        setDomainError(
          "Enter a valid business name to generate your trial subdomain.",
        );
        return false;
      }
      return true;
    }

    if (domainData.domainType === "existing") {
      const existingDomain = String(domainData.existingDomain || "")
        .trim()
        .toLowerCase();
      if (!isValidExistingDomain(existingDomain)) {
        setDomainError("Enter your existing domain, for example example.com.");
        return false;
      }
      return true;
    }

    if (domainExtensions.length === 0) {
      setDomainError(
        "No domain extensions are currently available. Please contact support.",
      );
      return false;
    }

    if (!domainData.customDomain || domainData.customDomain.length < 3) {
      setDomainError("Please enter a preferred domain name.");
      return false;
    }

    if (
      !domainExtensions.some(
        (extension) => extension.extension === domainData.selectedExtension,
      )
    ) {
      setDomainError("Please select an available domain extension.");
      return false;
    }

    if (!domainSettings.enable_availability_check) {
      if (!domainAvailabilityAcknowledged) {
        setDomainError(
          "Please confirm that you understand this domain's availability was not checked.",
        );
        return false;
      }
      return true;
    }

    if (domainData.availability?.serviceOffline) {
      setDomainError(
        "Domain availability could not be checked. Please try again.",
      );
      return false;
    }
    if (domainData.availability?.available !== true) {
      setDomainError("Please check that your preferred domain is available.");
      return false;
    }
    return true;
  };

  const validateAgreement = () => {
    if (!agreementAccepted) {
      setAgreementError("Please accept the terms and conditions to continue.");
      return false;
    }
    if (!legalFirstName.trim() || !legalSurname.trim()) {
      setAgreementError(
        "Enter your first name and surname to include on the contract.",
      );
      return false;
    }
    setAgreementError("");
    return true;
  };

  const getLegalFullName = () =>
    [legalFirstName.trim(), legalSurname.trim()].filter(Boolean).join(" ") ||
    formData.contactName.trim() ||
    "Client";

  const handleAgreementAcceptanceChange = (checked) => {
    setAgreementAccepted(checked);
    if (checked && !legalFirstName && !legalSurname) {
      const contactNameParts = formData.contactName.trim().split(/\s+/);
      setLegalFirstName(contactNameParts.shift() || "");
      setLegalSurname(contactNameParts.join(" "));
    }
    if (agreementError) setAgreementError("");
  };

  const getContractAcceptanceDetails = () => {
    const contract =
      contractTemplates.find((template) => template.id === "online-store") ||
      DEFAULT_CONTRACT_TEMPLATES[0];
    return {
      contractId: contract.id,
      contractName: contract.name,
      agreementNumber,
      acceptedAt: new Date().toISOString(),
      clientFirstName: legalFirstName.trim(),
      clientSurname: legalSurname.trim(),
      clientFullName: getLegalFullName(),
    };
  };

  const downloadDocument = (documentType) => {
    const printClass = `printing-${documentType}`;
    const clearPrintClass = () => {
      document.body.classList.remove(printClass);
      window.removeEventListener("afterprint", clearPrintClass);
    };

    document.body.classList.remove("printing-agreement", "printing-invoice");
    document.body.classList.add(printClass);
    window.addEventListener("afterprint", clearPrintClass);
    window.print();
  };

  // ============ NAVIGATION ============
  const getNextStep = (current) => {
    const total = getTotalSteps();
    if (current >= total) return total;
    return current + 1;
  };

  const handleNextStep = () => {
    const total = getTotalSteps();
    const nextStep = getNextStep(currentStep);
    const stepId = getStepIdByIndex(currentStep);

    if (stepId === "package" && validateStep1()) {
      setDirection(1);
      setCurrentStep(nextStep);
      setError("");
    } else if (stepId === "business" && validateStep2()) {
      setDirection(1);
      setCurrentStep(nextStep);
      setError("");
    } else if (stepId === "domain" && validateDomain()) {
      if (
        !isTrial &&
        domainData.domainType === "custom" &&
        !showDomainCostNotice
      ) {
        setShowDomainCostNotice(true);
        return;
      }
      setShowDomainCostNotice(false);
      setDirection(1);
      setCurrentStep(nextStep);
      setError("");
    } else if (stepId === "agreement" && validateAgreement()) {
      setDirection(1);
      setCurrentStep(nextStep);
      setError("");
    } else if (currentStep < total) {
      setDirection(1);
      setCurrentStep(nextStep);
      setError("");
    }
  };

  const handlePreviousStep = () => {
    setShowDomainCostNotice(false);
    if (currentStep > 1) {
      setDirection(-1);
      setCurrentStep(currentStep - 1);
      setError("");
      setValidationErrors({});
      setAgreementError("");
    } else if (currentStep === 1 && assessmentComplete) {
      setDirection(-1);
      setCurrentStep(0);
      setError("");
    }
  };

  // ============ SCROLL FUNCTIONS ============
  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });
  const scrollToBottom = () =>
    window.scrollTo({
      top: document.documentElement.scrollHeight,
      behavior: "smooth",
    });

  const getGuideSteps = () => {
    if (currentStep === 0) {
      return [
        {
          target: '[data-tour="readiness-assessment"]',
          title: "Your business readiness",
          description:
            "This assessment helps the team understand your goals and tailor your store setup. Answer the questions in each section, then finish the assessment.",
        },
      ];
    }

    const step = getStepByIndex(currentStep);
    const tours = {
      package: [
        {
          target: '[data-tour="package-grid"]',
          title: "Compare the packages",
          description:
            "Each card shows a plan's monthly price, setup fee, product limit, and included features. Select the card that fits your business.",
        },
        {
          target: '[data-tour="package-summary"]',
          title: "Confirm your selection",
          description: selectedPackageDetails
            ? `${selectedPackageDetails.Name} is selected at R${Number(selectedPackageDetails.Price).toLocaleString()}/${selectedPackageDetails.BillingPeriod || "month"}. You can return to change this before submitting.`
            : "Your selected plan and billing price will appear here after you choose a package.",
        },
        {
          target: '[data-tour="save-draft"]',
          title: "Save your progress",
          description:
            "Your answers save automatically while you work. Select Save as draft to save immediately, then restore your progress when you return.",
        },
        {
          target: '[data-tour="next-button"]',
          title: "Continue to business details",
          description:
            "Once a package is selected, use Next to enter your business and contact information.",
        },
      ],
      business: [
        {
          target: '[data-tour="business-details"]',
          title: "Business information",
          description:
            "Enter your business name and choose its type. These required details identify the store we will create.",
        },
        {
          target: '[data-tour="contact-details"]',
          title: "Main contact",
          description:
            "Provide the contact person's name, email, and phone number. We use these details to follow up about your setup.",
        },
        {
          target: '[data-tour="business-extra"]',
          title: "Optional business details",
          description:
            "Add your current website and a short description of what you sell. These details are optional but help the team prepare.",
        },
        {
          target: '[data-tour="next-button"]',
          title: "Continue to domain setup",
          description:
            "Next checks that the required business fields are complete before moving on.",
        },
      ],
      domain: isTrial
        ? [
            {
              target: '[data-tour="trial-domain"]',
              title: "Your trial store address",
              description:
                "This Toora Flex address is generated from your business name and is included with your trial. You do not need to buy a domain here.",
            },
            {
              target: '[data-tour="next-button"]',
              title: "Continue setup",
              description:
                "Select Next to continue to the store design. You can choose a custom domain later.",
            },
          ]
        : domainData.domainType === "existing"
          ? [
              {
                target: '[data-tour="domain-options"]',
                title: "Choose your domain option",
                description:
                  "Choose the option that matches your situation. A domain you already own has no registration charge.",
              },
              {
                target: '[data-tour="existing-domain"]',
                title: "Enter your existing domain",
                description:
                  "Enter the domain you already own, such as example.com. Do not include https:// or www.",
              },
              {
                target: '[data-tour="next-button"]',
                title: "Continue to store design",
                description:
                  "After entering a valid domain, select Next. No registration fee is added for an existing domain.",
              },
            ]
          : [
              {
                target: '[data-tour="domain-options"]',
                title: "Choose a new domain",
                description:
                  "Select the new-domain option if you want us to register a domain for your store. The yearly total is shown before you continue.",
              },
              {
                target: '[data-tour="custom-domain-name"]',
                title: "Enter a name and ending",
                description:
                  "Type the name only in the first field, then choose an ending such as .co.za or .com from the selector. Together they form your full domain.",
              },
              {
                target: domainSettings.enable_availability_check
                  ? '[data-tour="availability-check"]'
                  : '[data-tour="availability-acknowledgement"]',
                title: domainSettings.enable_availability_check
                  ? "Check that it is available"
                  : "Confirm the availability notice",
                description: domainSettings.enable_availability_check
                  ? "Check availability before continuing. If the name is taken, try another name or use a suggestion."
                  : "Availability checking is unavailable right now. Confirm the notice so the team can verify the domain before registration.",
              },
              {
                target: '[data-tour="domain-total"]',
                title: "Review the yearly total",
                description: `Your current selection is ${domainData.customDomain || "yourname"}${domainData.selectedExtension}. Your yearly domain charge is R${getDomainPriceWithTax(getDomainChargeBeforeTax()).toFixed(2)}${isLitePackage() ? " for the full domain price" : ` after the included .co.za allowance of R${getDomainPriceWithTax(getIncludedDomainAllowance()).toFixed(2)}`}.`,
              },
              {
                target: '[data-tour="next-button"]',
                title: "Continue to store design",
                description:
                  "When the domain is valid and available, select Next to continue with your store setup.",
              },
            ],
      design: [
        {
          target: '[data-tour="design-template"]',
          title: "Choose a store template",
          description:
            "Select the layout that best suits your products. You can customize colors and fonts below.",
        },
        {
          target: '[data-tour="design-colors"]',
          title: "Set your brand colors",
          description:
            "Choose primary and secondary colors for your store. You can also select a font and upload a logo or banner.",
        },
        {
          target: '[data-tour="design-assets"]',
          title: "Add your brand assets",
          description:
            "Upload your store logo and homepage banner if you have them. These are optional and can be added later.",
        },
        {
          target: '[data-tour="next-button"]',
          title: "Continue to products",
          description:
            "Review your design choices, then select Next. You can refine the design later.",
        },
      ],
      products: [
        {
          target: '[data-tour="products-header"]',
          title: "Add your first products",
          description:
            "Use Add Product to enter a name and price, then add stock and other details if available. Products can also be added later from your dashboard.",
        },
        {
          target: '[data-tour="next-button"]',
          title: "Continue to the agreement",
          description:
            "When you are ready, select Next. Adding products now is optional.",
        },
      ],
      agreement: [
        {
          target: '[data-tour="agreement-document"]',
          title: "Review the agreement",
          description:
            "Read the service agreement. Use Download Contract if you want to save a copy before accepting.",
        },
        {
          target: '[data-tour="agreement-acceptance"]',
          title: "Accept the terms",
          description:
            "Select the checkbox after reviewing the agreement. This is required to continue.",
        },
        {
          target: '[data-tour="next-button"]',
          title: "Continue to the invoice",
          description:
            "After accepting the agreement, select Next to review the invoice and payment options.",
        },
      ],
      invoice: [
        {
          target: '[data-tour="invoice-table"]',
          title: "Review your invoice",
          description:
            "Review the selected package charge and any applicable domain charge.",
        },
        ...(!isTrial
          ? [
              {
                target: '[data-tour="payment-methods"]',
                title: "Choose a payment method",
                description:
                  "Select how you want to pay. Follow any payment instructions shown here.",
              },
            ]
          : []),
        {
          target: '[data-tour="submit-request"]',
          title: isTrial ? "Start your free trial" : "Submit your request",
          description: isTrial
            ? "Select Start Free Trial to send your store request."
            : "When the invoice and payment option are correct, select this button to create your request and continue to payment.",
        },
        ...(!isTrial
          ? [
              {
                target: '[data-tour="pay-later"]',
                title: "Submit and pay later",
                description:
                  "Submit your request without paying now. Payment is required before store setup can begin; return to the request status page to pay.",
              },
            ]
          : []),
      ],
    };

    return (
      tours[step?.id] || [
        {
          target: '[data-tour="step-content"]',
          title: step?.title || step?.name || "Onboarding step",
          description:
            step?.description || "Review the information on this step.",
        },
      ]
    );
  };

  const guideSteps = getGuideSteps();
  const activeGuideStep = guideSteps[guideStepIndex] || guideSteps[0];

  useEffect(() => {
    if (!isGuideOpen || !activeGuideStep) {
      setGuideTargetRect(null);
      return undefined;
    }

    const target =
      document.querySelector(activeGuideStep.target) ||
      (activeGuideStep.fallbackTarget
        ? document.querySelector(activeGuideStep.fallbackTarget)
        : null) ||
      document.querySelector('[data-tour="step-content"]');

    if (!target) {
      setGuideTargetRect(null);
      return undefined;
    }

    target.classList.add("guided-tour-highlight");
    target.scrollIntoView({ behavior: "smooth", block: "center" });

    const updateTargetRect = () => {
      const rect = target.getBoundingClientRect();
      setGuideTargetRect({
        top: rect.top,
        right: rect.right,
        bottom: rect.bottom,
        left: rect.left,
        width: rect.width,
      });
    };
    const updateTimer = window.setTimeout(updateTargetRect, 220);
    window.addEventListener("scroll", updateTargetRect, true);
    window.addEventListener("resize", updateTargetRect);

    return () => {
      window.clearTimeout(updateTimer);
      window.removeEventListener("scroll", updateTargetRect, true);
      window.removeEventListener("resize", updateTargetRect);
      target.classList.remove("guided-tour-highlight");
    };
  }, [
    isGuideOpen,
    currentStep,
    guideStepIndex,
    activeGuideStep?.target,
    domainData.domainType,
  ]);

  useEffect(() => {
    if (isGuideOpen) setGuideStepIndex(0);
  }, [currentStep]);

  useEffect(() => {
    if (!isGuideOpen) return undefined;
    const closeGuideOnEscape = (event) => {
      if (event.key === "Escape") setIsGuideOpen(false);
    };
    window.addEventListener("keydown", closeGuideOnEscape);
    return () => window.removeEventListener("keydown", closeGuideOnEscape);
  }, [isGuideOpen]);

  const getRenderedContract = (acceptance = getContractAcceptanceDetails()) => {
    const contract =
      contractTemplates.find((template) => template.id === "online-store") ||
      DEFAULT_CONTRACT_TEMPLATES[0];
    const domainBasePrice =
      !isTrial && domainData.domainType === "custom" ? getDomainBasePrice() : 0;
    const domainChargeBeforeTax = getDomainChargeBeforeTax();
    const domainTotal = getDomainPriceWithTax(domainChargeBeforeTax);
    const packageCharge = isTrial
      ? 0
      : Number(selectedPackageDetails?.Price || 0);
    const upfrontTotal = isTrial
      ? 0
      : roundMoney(packageCharge + domainTotal);
    const includedDomainCredit = roundMoney(
      getDomainPriceWithTax(domainBasePrice) -
        getDomainPriceWithTax(domainChargeBeforeTax),
    );
    const domainListTotal = getDomainPriceWithTax(domainBasePrice);
    const monthlyPackageFee = Number(selectedPackageDetails?.Price || 0);
    const billingSummary = isTrial
      ? "Trial onboarding: R0.00 is due now. The trial uses an included Toora Flex subdomain and has no paid package subscription fee during the trial."
      : [
          `Selected package: ${selectedPackageDetails?.Name || "Selected package"} (${selectedPackageDetails?.Tier || "standard"}).`,
          `Package charge due now: R${packageCharge.toFixed(2)} per ${selectedPackageDetails?.BillingPeriod || "month"}.`,
          domainData.domainType === "existing"
            ? "Domain registration charge: R0.00 (client-provided existing domain)."
            : `Annual selected domain list price: R${domainListTotal.toFixed(2)}.`,
          isLitePackage()
            ? "Included annual domain allowance: R0.00 (Lite does not include a free custom domain)."
            : `Included annual domain allowance: R${includedDomainCredit.toFixed(2)}.`,
          `Annual domain charge after allowance: R${getDomainPriceWithTax(domainChargeBeforeTax).toFixed(2)}.`,
          ...(domainData.domainType === "custom"
            ? [
                "GoDaddy domain availability and pricing may change before registration. Final availability and cost will be confirmed at registration; any price change will be confirmed with the Client before proceeding.",
              ]
            : []),
          `Total due now: R${upfrontTotal.toFixed(2)}.`,
          `The selected package charge is R${monthlyPackageFee.toFixed(2)} per ${selectedPackageDetails?.BillingPeriod || "month"} and is included in the total due now.`,
        ].join("\n");
    const agreementDate = new Date(acceptance.acceptedAt).toLocaleDateString(
      "en-ZA",
      {
        day: "2-digit",
        month: "long",
        year: "numeric",
      },
    );
    const domainName = isTrial
      ? `${domainData.subdomain}.tooraflex.co.za`
      : domainData.domainType === "existing"
        ? domainData.existingDomain
        : `${domainData.customDomain}${domainData.selectedExtension}`;
    const contractFields = {
      "{{AGREEMENT_DATE}}": agreementDate,
      "{{AGREEMENT_NUMBER}}": acceptance.agreementNumber,
      "{{CLIENT_FULL_NAME}}": acceptance.clientFullName || getLegalFullName(),
      "{{BUSINESS_NAME}}": formData.businessName || "Client",
      "{{CONTACT_NAME}}": formData.contactName || "Not provided",
      "{{PHONE}}": formData.phone || "Not provided",
      "{{EMAIL}}": formData.email || user?.Email || "Not provided",
      "{{PACKAGE_NAME}}": `${selectedPackageDetails?.Name || "Selected package"}${selectedPackageDetails?.Tier ? ` (${selectedPackageDetails.Tier})` : ""}`,
      "{{PRODUCT_LIMIT}}": selectedPackageDetails?.ProductLimit || "200",
      "{{MONTHLY_FEE}}": `R${monthlyPackageFee.toFixed(2)} per ${selectedPackageDetails?.BillingPeriod || "month"} (charged now)`,
      "{{SETUP_FEE}}": "R0.00",
      "{{DOMAIN_FEE}}": isTrial
        ? `Trial subdomain: ${domainName} (included)`
        : domainData.domainType === "existing"
          ? `Existing domain: ${domainName} (client-provided; no registration fee)`
          : isLitePackage()
            ? `New domain: ${domainName} — R${domainTotal.toFixed(2)} per year (full domain price)`
            : `New domain: ${domainName} — included domain allowance up to R${getDomainPriceWithTax(getIncludedDomainAllowance()).toFixed(2)} per year; extension upgrade R${domainTotal.toFixed(2)} per year`,
      "{{UPFRONT_DUE}}": isTrial
        ? "R0.00 (trial)"
        : `R${upfrontTotal.toFixed(2)}`,
      "{{DOMAIN_TERMS}}": isTrial
        ? `A Toora Flex trial subdomain is generated from the business name: ${domainName}.`
        : domainData.domainType === "existing"
          ? `The Client has provided the existing domain ${domainName}. No domain registration fee is charged.`
          : isLitePackage()
            ? `The Lite package does not include a free custom domain. The selected domain ${domainName} is charged at its full annual price of R${domainTotal.toFixed(2)} including VAT.`
            : `The ${selectedPackageDetails?.Name || "selected"} package includes a free annual domain allowance equal to the current .co.za price (R${getDomainPriceWithTax(getIncludedDomainAllowance()).toFixed(2)} including VAT). The selected domain ${domainName} costs R${getDomainPriceWithTax(domainBasePrice).toFixed(2)} per year. The allowance applied is R${includedDomainCredit.toFixed(2)}, so the annual domain upgrade charge is R${domainTotal.toFixed(2)} including VAT.`,
      "{{ONBOARDING_BILLING_SUMMARY}}": billingSummary,
      "{{TRIAL_TERMS}}": isTrial
        ? "The selected trial has no upfront charge and uses a Toora Flex subdomain only. A custom domain and paid plan may be selected after the trial."
        : "Not applicable. The selected plan is a paid subscription.",
    };
    let content = Object.entries(contractFields).reduce(
      (text, [token, value]) => text.split(token).join(String(value)),
      contract.content,
    );
    if (!contract.content.includes("{{ONBOARDING_BILLING_SUMMARY}}")) {
      const billingSection = `\n4.6. Onboarding billing summary\n${billingSummary}\n`;
      const implementationHeading =
        "\n5. IMPLEMENTATION, REVIEW AND ACCEPTANCE";
      content = content.includes(implementationHeading)
        ? content.replace(
            implementationHeading,
            `${billingSection}${implementationHeading}`,
          )
        : `${content}\n${billingSection}`;
    }

    return {
      id: contract.id,
      name: contract.name,
      title: contract.title,
      content,
    };
  };

  // ============ SUBMIT ============
  const handleSubmit = async (e, payLater = false, payAfterEdit = false) => {
    e.preventDefault();

    // Add validation before submitting
    if (!formData.businessName.trim()) {
      setError("Please enter your business name before submitting.");
      return;
    }

    if (!formData.contactName.trim()) {
      setError("Please enter your contact name before submitting.");
      return;
    }

    if (!formData.email.trim()) {
      setError("Please enter your email address before submitting.");
      return;
    }

    if (!validateStep2()) {
      const businessStepIndex = getEnabledSteps().findIndex(
        (step) => step.id === "business",
      );
      if (businessStepIndex >= 0) {
        setCurrentStep(businessStepIndex + 1);
      }
      return;
    }

    if (!validateAgreement()) {
      const agreementStepIndex = getEnabledSteps().findIndex(
        (step) => step.id === "agreement",
      );
      if (agreementStepIndex >= 0) {
        setCurrentStep(agreementStepIndex + 1);
      }
      return;
    }

    if (!validateDomain()) {
      const domainStepIndex = getEnabledSteps().findIndex(
        (step) => step.id === "domain",
      );
      if (domainStepIndex >= 0) {
        setCurrentStep(domainStepIndex + 1);
      } else {
        setError(
          "Domain details are required before submitting. Please contact support to enable the domain step.",
        );
      }
      return;
    }

    try {
      setSubmitting(true);
      setError("");
      setSuccess("");

      const domainBasePrice =
        !isTrial && domainData.domainType === "custom"
          ? getDomainBasePrice()
          : 0;
      const domainChargeBeforeTax = getDomainChargeBeforeTax();
      const domainVat = getDomainTax(domainChargeBeforeTax);
      const submittedDomainData = {
        ...domainData,
        domainPrice: getDomainPriceWithTax(domainChargeBeforeTax),
        domainPriceBeforeTax: domainChargeBeforeTax,
        domainListPriceBeforeTax: domainBasePrice,
        domainAllowanceBeforeTax: isLitePackage()
          ? 0
          : Math.min(domainBasePrice, getIncludedDomainAllowance()),
        domainTax: domainVat,
        domainPriceIncludesTax: true,
        isFree:
          isTrial ||
          domainData.domainType === "existing" ||
          (domainData.domainType === "custom" && domainChargeBeforeTax === 0),
      };
      const invoiceSetupFee = 0;
      const invoiceDomainTotal = getDomainPriceWithTax(domainChargeBeforeTax);
      const packageCharge = isTrial
        ? 0
        : Number(selectedPackageDetails?.Price || 0);
      const invoiceTotalDue = roundMoney(packageCharge + invoiceDomainTotal);
      const acceptanceDetails = getContractAcceptanceDetails();
      const contractDocument = getRenderedContract(acceptanceDetails);
      const payload = {
        packageId: selectedPackage,
        isTrial,
        domainPriceNoticeAcknowledged,
        businessInfo: {
          businessName: formData.businessName || "",
          businessType: formData.businessType || "",
          contactName: formData.contactName || "",
          email: formData.email || user?.Email || "",
          phone: formData.phone || "",
          website: formData.website || "",
          description: formData.description || "",
        },
        domain: {
          ...submittedDomainData,
          domainType: isTrial ? "subdomain" : domainData.domainType,
          customDomain: isTrial ? "" : domainData.customDomain,
          yearsToRegister: domainData.yearsToRegister || 1,
        },
        design: designData,
        products: products,
        agreementAccepted: agreementAccepted,
        agreedAt: acceptanceDetails.acceptedAt,
        contractAcceptance: acceptanceDetails,
        domainSettings: {
          availabilityCheckEnabled: domainSettings.enable_availability_check,
          dnsConfigEnabled: domainSettings.enable_dns_config,
        },
        additionalRequirements: {
          onboardingAssessment: assessmentAnswers,
          package: {
            packageId: selectedPackage,
            name: selectedPackageDetails?.Name || "",
            tier: selectedPackageDetails?.Tier || "",
            price: Number(selectedPackageDetails?.Price || 0),
            billingPeriod: selectedPackageDetails?.BillingPeriod || "month",
            setupFee: Number(selectedPackageDetails?.SetupFee || 0),
            productLimit: selectedPackageDetails?.ProductLimit || "200",
          },
          businessInfo: {
            ...formData,
            email: formData.email || user?.Email || "",
            legalFirstName: legalFirstName.trim(),
            legalSurname: legalSurname.trim(),
            legalFullName: getLegalFullName(),
          },
          contractAcceptance: acceptanceDetails,
          agreement: {
            accepted: agreementAccepted,
            agreedAt: acceptanceDetails.acceptedAt,
            contract: acceptanceDetails,
          },
          contractDocument,
          design: {
            template: designData.template,
            primaryColor: designData.primaryColor,
            secondaryColor: designData.secondaryColor,
            font: designData.font,
            logoFileName: designData.logo?.name || "",
            bannerFileName: designData.banner?.name || "",
          },
          products: products.map((product) => ({
            ...product,
            image:
              typeof product.image === "string" ? product.image : undefined,
            imageFileName: product.image?.name || product.imageFileName || "",
          })),
          invoice: {
            invoiceNumber,
            clientFullName: getLegalFullName(),
            packageAmount: packageCharge,
            packageSetupFee: invoiceSetupFee,
            domainTotal: invoiceDomainTotal,
            monthlyPackageAmount: isTrial
              ? 0
              : Number(selectedPackageDetails?.Price || 0),
            packageBillingPeriod:
              selectedPackageDetails?.BillingPeriod || "month",
            isLiteTier: isLitePackage(),
            domainListPrice: getDomainPriceWithTax(domainBasePrice),
            domainAllowance: isLitePackage()
              ? 0
              : roundMoney(
                  getDomainPriceWithTax(domainBasePrice) - invoiceDomainTotal,
                ),
            totalDue: invoiceTotalDue,
            currency: "ZAR",
            paymentMethod: selectedPaymentMethod || "",
            packageName: selectedPackageDetails?.Name || "",
            packageTier: selectedPackageDetails?.Tier || "",
          },
          domain: {
            ...submittedDomainData,
            domainType: isTrial ? "subdomain" : domainData.domainType,
            customDomain: isTrial ? "" : domainData.customDomain,
            availabilityAcknowledged: domainAvailabilityAcknowledged,
            availabilityChecked:
              isTrial ||
              domainData.domainType === "existing" ||
              !domainSettings.enable_availability_check ||
              domainData.availability?.available === true,
            isTrial,
          },
        },
      };

      if (editRequestId) {
        await storeRequestApi.updateRequest(editRequestId, payload);
        if (payAfterEdit && getCurrentPaymentDue() > 0) {
          if (!selectedPaymentMethod) {
            throw new Error("Choose an available payment option to continue.");
          }
          const paymentResponse = await paymentApi.startPayment(
            editRequestId,
            selectedPaymentMethod,
          );
          const paymentData = paymentResponse.data;
          setPaymentResult(paymentData.payment);
          setGatewayCheckout(paymentData.checkout);
          setRedirectingToGateway(Boolean(paymentData.checkout));
          await clearDraft();
          setDraftCompleted(true);
          if (!paymentData.checkout) {
            setSuccess(
              "Your payment reference is ready. Follow the instructions below; an administrator will confirm receipt.",
            );
          }
          return;
        }
        await clearDraft();
        setDraftCompleted(true);
        navigate(`/request-status/${editRequestId}`);
        return;
      }

      let requestId = submittedRequestId;
      if (!requestId) {
        const response = await fetch("/api/v1/store-requests", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: JSON.stringify(payload),
        });

        const data = await response.json();

        if (data.success) {
          requestId = data.data?.StoreRequestID;
          if (!requestId) {
            throw new Error("The store request was not created successfully.");
          }
          setSubmittedRequestId(requestId);
        } else {
          throw new Error(
            data.error || data.message || "Failed to submit request.",
          );
        }
      }

      if (isTrial) {
        await clearDraft();
        setDraftCompleted(true);
        navigate(`/request-status/${requestId}`);
        return;
      }
      if (payLater || invoiceTotalDue <= 0) {
        await clearDraft();
        setDraftCompleted(true);
        navigate(`/request-status/${requestId}`);
        return;
      }
      await storeRequestApi.saveDraft({
        ...draftData,
        submittedRequestId: requestId,
      });
      if (!selectedPaymentMethod) {
        throw new Error("Choose an available payment option to continue.");
      }

      const paymentResponse = await paymentApi.startPayment(
        requestId,
        selectedPaymentMethod,
      );
      const paymentData = paymentResponse.data;
      setPaymentResult(paymentData.payment);
      setGatewayCheckout(paymentData.checkout);
      setRedirectingToGateway(Boolean(paymentData.checkout));
      await clearDraft();
      setDraftCompleted(true);
      setSuccess(
        paymentData.checkout
          ? "Your request is ready. Redirecting you to the payment gateway."
          : "Your payment reference is ready. Follow the instructions below; an administrator will confirm receipt.",
      );
    } catch (err) {
      console.error("Error submitting request:", err);
      setError(
        err.response?.data?.error ||
          err.message ||
          "An error occurred. Please try again later.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  // ============ HELPER FUNCTIONS ============
  const getFeatureEntries = (features) => {
    if (!features) return [];
    let featuresObj = features;
    if (typeof features === "string") {
      try {
        featuresObj = JSON.parse(features);
      } catch (e) {
        return [];
      }
    }
    if (typeof featuresObj !== "object" || Array.isArray(featuresObj))
      return [];
    return Object.entries(featuresObj)
      .map(([key, value]) => {
        if (typeof value === "string") return { key, value: value.trim() };
        if (typeof value === "number" && Number.isFinite(value)) {
          return { key, value: String(value) };
        }
        if (value === true) return { key, value: "Included" };
        return { key, value: "" };
      })
      .filter(({ value }) => value !== "");
  };

  // ============ SLIDE ANIMATIONS ============
  const slideVariants = {
    enter: (direction) => ({ x: direction > 0 ? 40 : -40, opacity: 0 }),
    center: {
      x: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 400,
        damping: 30,
        duration: 0.3,
      },
    },
    exit: (direction) => ({
      x: direction < 0 ? 40 : -40,
      opacity: 0,
      transition: {
        type: "spring",
        stiffness: 400,
        damping: 30,
        duration: 0.2,
      },
    }),
  };

  const fadeVariants = {
    enter: { opacity: 0 },
    center: {
      opacity: 1,
      transition: { duration: 0.25 },
    },
    exit: {
      opacity: 0,
      transition: { duration: 0.18 },
    },
  };

  // ============ RENDER FUNCTIONS ============
  const renderStepIndicator = () => {
    const enabledSteps = getEnabledSteps();
    const total = enabledSteps.length;

    if (total === 0) return null;

    return (
      <div className="step-indicator-compact">
        <p className="step-indicator-caption">Store setup steps</p>
        <div className="step-progress-bar">
          <div
            className="step-progress-fill"
            style={{
              width: `${total <= 1 ? 100 : ((currentStep - 1) / (total - 1)) * 100}%`,
            }}
          />
        </div>
        <div className="step-dots">
          {enabledSteps.map((step, index) => {
            const stepNumber = index + 1;
            const isCompleted = stepNumber < currentStep;
            const isActive = stepNumber === currentStep;
            const stepIcon = step.icon || stepNumber;

            return (
              <div
                key={step.id || stepNumber}
                className={`step-dot ${isActive ? "active" : ""} ${isCompleted ? "completed" : ""}`}
                onClick={() => {
                  if (isCompleted) {
                    setDirection(stepNumber > currentStep ? 1 : -1);
                    setCurrentStep(stepNumber);
                  }
                }}
                style={{
                  cursor: isCompleted ? "pointer" : "default",
                }}
              >
                <span className="step-dot-number">{stepIcon}</span>
                <span className="step-dot-label">
                  Step {stepNumber}: {step.name || "Setup"}
                </span>
                {isCompleted && <span className="step-dot-check">✓</span>}
              </div>
            );
          })}
        </div>
        <div className="step-counter">
          Step {currentStep} of {total}
        </div>
      </div>
    );
  };

  // ============ DYNAMIC STEP RENDERER ============
  const renderStepContent = () => {
    if (currentStep === 0) {
      return (
        <motion.div
          key="business-profile"
          variants={fadeVariants}
          initial="enter"
          animate="center"
          exit="exit"
          className="step-content"
          data-tour="step-content"
        >
          <div className="step-header-compact">
            <h2>Before Step 1: Business Readiness</h2>
            <p>
              Answer 9 quick questions so our team can understand your store
              request and plan the right launch support.
            </p>
          </div>
          <BusinessProfileStep
            initialData={assessmentAnswers}
            onChange={setAssessmentAnswers}
            guideTarget="readiness-assessment"
            renderNavigation={({ canGoBack, isLastPage, onBack, onNext }) => (
              <div
                className={`step-navigation-compact readiness-step-navigation has-back ${
                  draftEnabled ? "has-draft" : "no-draft"
                }`}
              >
                <button
                  type="button"
                  className="btn-nav back"
                  onClick={onBack}
                  disabled={!canGoBack}
                  aria-label="Back"
                >
                  <i className="fas fa-arrow-left" aria-hidden="true" />
                  Back
                </button>
                {draftStatus && (
                  <span className="request-draft-feedback" role="status">
                    {draftStatus}
                  </span>
                )}
                <div className="nav-spacer" />
                <div className="request-next-actions">
                  <button
                    type="button"
                    className="btn-nav next"
                    data-tour="next-button"
                    onClick={onNext}
                  >
                    {isLastPage ? "Proceed" : "Next"}
                    {!isLastPage && (
                      <i className="fas fa-arrow-right" aria-hidden="true" />
                    )}
                  </button>
                </div>
              </div>
            )}
            onFinish={() => {
              setAssessmentComplete(true);
              setDirection(1);
              setCurrentStep(1);
              setError("");
            }}
          />
        </motion.div>
      );
    }

    const stepData = getStepByIndex(currentStep);
    const stepId = stepData?.id;

    if (stepId === "package") {
      return renderStep1();
    }

    if (stepId === "business") {
      return renderStep2();
    }

    if (stepId === "domain") {
      return renderStep3();
    }

    if (stepId === "design") {
      return renderStep4();
    }

    if (stepId === "products") {
      return renderStep5();
    }

    if (stepId === "agreement") {
      return renderStep6();
    }

    if (stepId === "invoice") {
      return renderStep7();
    }

    // Fallback
    return (
      <motion.div
        key={`step-${currentStep}`}
        custom={direction}
        variants={slideVariants}
        initial="enter"
        animate="center"
        exit="exit"
        className="step-content"
        data-tour="step-content"
      >
        <div className="step-header-compact">
          <h2>{stepData?.title || stepData?.name || `Step ${currentStep}`}</h2>
          <p>{stepData?.description || ""}</p>
        </div>
        <div className="step-form">
          <p className="hint-text">
            This step is being configured by the administrator.
          </p>
        </div>
      </motion.div>
    );
  };

  // ============ STEP 1: Package Selection ============
  const renderStep1 = () => (
    <motion.div
      key="step1"
      custom={direction}
      variants={slideVariants}
      initial="enter"
      animate="center"
      exit="exit"
      className="step-content"
      data-tour="step-content"
    >
      <div className="step-header-compact">
        <h2>Choose Your Package</h2>
        <p>Select the plan that best fits your business needs</p>
      </div>

      {validationErrors.package && (
        <div className="validation-error-message">
          <i className="fas fa-exclamation-circle"></i>
          {validationErrors.package}
        </div>
      )}

      {packages.length === 0 ? (
        <div className="no-packages-message">
          <i className="fas fa-box-open"></i>
          <h3>No Packages Available</h3>
          <p>Please check back later for our available packages.</p>
        </div>
      ) : (
        <>
          <div className="package-grid-compact" data-tour="package-grid">
            {packages.map((pkg) => {
              const isSelected = selectedPackage === pkg.PackageID;
              const isPopular = String(pkg.Tier).toLowerCase() === "growth";
              const featureEntries = getFeatureEntries(pkg.Features).filter(
                ({ key, value }) =>
                  !(
                    key.toLowerCase().includes("domain") &&
                    /free|included|complimentary/i.test(value)
                  ),
              );

              return (
                <div
                  key={pkg.PackageID}
                  className={`package-card-compact ${isSelected ? "selected" : ""} ${isPopular ? "popular" : ""}`}
                  onClick={() => handlePackageSelect(pkg.PackageID)}
                >
                  {isPopular && (
                    <div className="popular-badge-compact">POPULAR</div>
                  )}
                  <div className="package-card-header-compact">
                    <h4>{pkg.Name}</h4>
                    <span className="package-tier-compact">{pkg.Tier}</span>
                  </div>
                  <div className="package-price-compact">
                    R{Number(pkg.Price).toLocaleString()}
                    <span>/{pkg.BillingPeriod || "month"}</span>
                  </div>
                  {pkg.SetupFee > 0 && (
                    <div className="package-setup-fee-compact">
                      <i className="fas fa-wallet"></i>
                      Setup: R{Number(pkg.SetupFee).toLocaleString()}
                    </div>
                  )}
                  <ul className="package-features-compact">
                    <li>
                      <i className="fas fa-check"></i>
                      <span>
                        {pkg.ProductLimit
                          ? `${pkg.ProductLimit} products`
                          : "Unlimited"}
                      </span>
                    </li>
                    {featureEntries.slice(0, 3).map(({ key, value }) => (
                      <li key={key}>
                        <i className="fas fa-check"></i>
                        <span>{value}</span>
                      </li>
                    ))}
                    {featureEntries.length > 3 && (
                      <li className="more-features">
                        +{featureEntries.length - 3} more
                      </li>
                    )}
                  </ul>
                  {isSelected && (
                    <div className="selected-indicator-compact">
                      <i className="fas fa-check-circle"></i> Selected
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div
            className="selected-package-summary-compact"
            data-tour="package-summary"
          >
            {selectedPackageDetails ? (
              <>
                <div className="summary-icon-compact">
                  <i className="fas fa-check-circle"></i>
                </div>
                <div className="summary-content-compact">
                  <strong>{selectedPackageDetails.Name} Selected</strong>
                  <span>
                    R{Number(selectedPackageDetails.Price).toLocaleString()}/
                    {selectedPackageDetails.BillingPeriod || "month"}
                    {isTrial && " • Trial subdomain included"}
                  </span>
                </div>
              </>
            ) : (
              <div className="summary-content-compact">
                <strong>No package selected yet</strong>
                <span>
                  Select a package above to see your plan and billing price.
                </span>
              </div>
            )}
          </div>
        </>
      )}
    </motion.div>
  );

  // ============ STEP 2: Business Details ============
  const renderStep2 = () => (
    <motion.div
      key="step2"
      custom={direction}
      variants={slideVariants}
      initial="enter"
      animate="center"
      exit="exit"
      className="step-content"
      data-tour="step-content"
    >
      <div className="step-header-compact">
        <h2>Tell Us About Your Business</h2>
        <p>Provide the details we need to set up your store</p>
      </div>

      <div className="request-form">
        <div className="form-row" data-tour="business-details">
          <div className="form-group">
            <label htmlFor="businessName">
              Business Name <span className="required">*</span>
            </label>
            <input
              type="text"
              id="businessName"
              name="businessName"
              value={formData.businessName}
              onChange={handleInputChange}
              placeholder="Your business name"
              className={validationErrors.businessName ? "error" : ""}
              required
            />
            {validationErrors.businessName && (
              <span className="field-error">
                {validationErrors.businessName}
              </span>
            )}
          </div>
          <div className="form-group">
            <label htmlFor="businessType">
              Business Type <span className="required">*</span>
            </label>
            <select
              id="businessType"
              name="businessType"
              value={formData.businessType}
              onChange={handleInputChange}
              className={validationErrors.businessType ? "error" : ""}
              required
            >
              <option value="">Select business type</option>
              <option value="Retail">Retail</option>
              <option value="Fashion">Fashion</option>
              <option value="Electronics">Electronics</option>
              <option value="Food & Beverage">Food & Beverage</option>
              <option value="Health & Beauty">Health & Beauty</option>
              <option value="Home & Living">Home & Living</option>
              <option value="Services">Services</option>
              <option value="Other">Other</option>
            </select>
            {validationErrors.businessType && (
              <span className="field-error">
                {validationErrors.businessType}
              </span>
            )}
          </div>
        </div>

        <div className="form-row" data-tour="contact-details">
          <div className="form-group">
            <label htmlFor="contactName">
              Contact Name <span className="required">*</span>
            </label>
            <input
              type="text"
              id="contactName"
              name="contactName"
              value={formData.contactName}
              onChange={handleInputChange}
              placeholder="Full name"
              className={validationErrors.contactName ? "error" : ""}
              required
            />
            {validationErrors.contactName && (
              <span className="field-error">
                {validationErrors.contactName}
              </span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="email">
              Email Address <span className="required">*</span>
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder="you@email.com"
              className={validationErrors.email ? "error" : ""}
              disabled={!!user?.Email}
              required
            />
            {validationErrors.email && (
              <span className="field-error">{validationErrors.email}</span>
            )}
            {user?.Email && (
              <small className="help-text">Using your account email</small>
            )}
          </div>
        </div>

        <div className="form-row" data-tour="business-extra">
          <div className="form-group">
            <label htmlFor="phone">
              Phone Number <span className="required">*</span>
            </label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              placeholder="+27 82 123 4567"
              className={validationErrors.phone ? "error" : ""}
              required
            />
            {validationErrors.phone && (
              <span className="field-error">{validationErrors.phone}</span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="website">Current Website (if any)</label>
            <input
              type="url"
              id="website"
              name="website"
              value={formData.website}
              onChange={handleInputChange}
              placeholder="https://yourwebsite.com"
            />
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="description">Business Description</label>
          <textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            placeholder="Tell us a bit about your business and what you plan to sell..."
            rows="3"
          />
        </div>
      </div>
    </motion.div>
  );

  // ============ STEP 3: Domain Setup ============
  const renderStep3 = () => {
    const domainPrice = getDomainPriceWithTax(getDomainChargeBeforeTax());
    const isServiceOffline = domainData.availability?.serviceOffline || false;

    return (
      <motion.div
        key="step3"
        custom={direction}
        variants={slideVariants}
        initial="enter"
        animate="center"
        exit="exit"
        className="step-content"
        data-tour="step-content"
      >
        <div className="step-header-compact">
          <h2>Domain Setup</h2>
          <p>
            {isTrial
              ? "Your trial includes a Toora Flex subdomain generated from your business name."
              : "Tell us whether you already own a domain or would like to register your preferred domain."}
          </p>
          {isTrial && (
            <div
              style={{
                marginTop: "8px",
                padding: "8px 14px",
                background: "#f0fdf4",
                borderRadius: "8px",
                border: "1px solid #86efac",
                color: "#16a34a",
                fontSize: "13px",
              }}
            >
              <i className="fas fa-gift" style={{ marginRight: "8px" }}></i>
              Your trial includes a Toora Flex subdomain.
            </div>
          )}
        </div>

        <div className="step-form">
          {domainError && (
            <div className="error-text domain-error">{domainError}</div>
          )}
          {showDomainCostNotice && domainData.domainType === "custom" && (
            <div className="availability-disabled-notice" role="alert">
              <i className="fas fa-triangle-exclamation" aria-hidden="true" />
              <div>
                <strong>Domain availability and pricing can change</strong>
                <p>
                  GoDaddy availability and prices may change before the domain
                  is registered. The final availability and cost will be
                  confirmed at registration; we will confirm any price change
                  with you before proceeding.
                </p>
                <button
                  type="button"
                  className="btn-primary"
                  onClick={() => {
                    setShowDomainCostNotice(false);
                    setDirection(1);
                    setCurrentStep(getNextStep(currentStep));
                    setError("");
                  }}
                >
                  I understand, continue
                </button>
              </div>
            </div>
          )}

          {!isTrial && (
            <div className="domain-guide">
              <span className="domain-guide-icon" aria-hidden="true">
                <i className="fas fa-compass" />
              </span>
              <div>
                <strong>Choose the option that fits you</strong>
                <p>
                  Use a domain you already own without a registration charge, or
                  register a new name. Lite pays the selected extension's full
                  annual price; higher tiers include a .co.za-priced allowance,
                  with only any upgrade difference charged.
                </p>
              </div>
            </div>
          )}

          {/* Show notice if availability check is disabled */}
          {!isTrial &&
            domainData.domainType === "custom" &&
            !domainSettings.enable_availability_check && (
              <div className="availability-disabled-notice">
                <i className="fas fa-info-circle"></i>
                Domain availability checking is disabled. You can continue after
                acknowledging that your preferred domain has not been checked.
              </div>
            )}

          {!isTrial && (
            <div className="domain-options" data-tour="domain-options">
              <button
                type="button"
                className={`domain-option ${domainData.domainType === "existing" ? "selected" : ""}`}
                aria-pressed={domainData.domainType === "existing"}
                onClick={() => {
                  setDomainData((prev) => ({
                    ...prev,
                    domainType: "existing",
                    availability: null,
                    verificationStatus: null,
                  }));
                  setShowDomainCostNotice(false);
                  setDomainAvailabilityAcknowledged(false);
                  setDomainError("");
                }}
              >
                <span className="domain-option-icon" aria-hidden="true">
                  <i className="fas fa-link" />
                </span>
                <span className="domain-option-copy">
                  <strong>I already own a domain</strong>
                  <small>
                    Enter the domain you already use. No registration charge.
                  </small>
                </span>
                <span className="domain-option-example">example.com</span>
                <span className="domain-option-check" aria-hidden="true">
                  <i className="fas fa-check" />
                </span>
              </button>
              <button
                type="button"
                className={`domain-option ${domainData.domainType === "custom" ? "selected" : ""}`}
                aria-pressed={domainData.domainType === "custom"}
                onClick={() => {
                  setDomainData((prev) => ({
                    ...prev,
                    domainType: "custom",
                    availability: null,
                    verificationStatus: null,
                  }));
                  setShowDomainCostNotice(false);
                  setDomainAvailabilityAcknowledged(false);
                  setDomainError("");
                }}
              >
                <span className="domain-option-icon" aria-hidden="true">
                  <i className="fas fa-globe" />
                </span>
                <span className="domain-option-copy">
                  <strong>I need to register a new domain</strong>
                  <small>
                    Choose a name and extension. The yearly price is shown
                    before you continue; your package's domain allowance is
                    applied automatically.
                  </small>
                </span>
                <span className="domain-price-tag">
                  {isLitePackage()
                    ? `From R${domainPrice.toFixed(2)}/year`
                    : domainPrice > 0
                      ? `Upgrade from R${domainPrice.toFixed(2)}/year`
                      : "Domain included"}
                </span>
                <span className="domain-option-check" aria-hidden="true">
                  <i className="fas fa-check" />
                </span>
              </button>
            </div>
          )}

          {isTrial && domainData.domainType === "subdomain" && (
            <div
              className="form-group domain-entry-card"
              data-tour="trial-domain"
            >
              <div className="domain-entry-heading">
                <span className="domain-entry-icon" aria-hidden="true">
                  <i className="fas fa-globe" />
                </span>
                <div>
                  <strong>Your store address</strong>
                  <small>Free subdomain included with your store</small>
                </div>
              </div>
              <label htmlFor="subdomain">Your trial store address</label>
              <div className="subdomain-input">
                <input
                  type="text"
                  id="subdomain"
                  value={domainData.subdomain}
                  readOnly
                />
                <span className="subdomain-suffix">.tooraflex.co.za</span>
              </div>
              <small className="help-text">
                Generated from your business name.
              </small>
            </div>
          )}

          {domainData.domainType === "existing" && !isTrial && (
            <div
              className="form-group domain-entry-card"
              data-tour="existing-domain"
            >
              <div className="domain-next-step">
                <span className="domain-step-number">Next</span>
                <span>Enter the domain you already own.</span>
              </div>
              <label htmlFor="existing-domain">Your existing domain *</label>
              <input
                type="text"
                id="existing-domain"
                value={domainData.existingDomain}
                onChange={(event) => {
                  setDomainData((prev) => ({
                    ...prev,
                    existingDomain: event.target.value
                      .trim()
                      .toLowerCase()
                      .replace(/^https?:\/\//, "")
                      .replace(/^www\./, "")
                      .split("/")[0],
                  }));
                  setShowDomainCostNotice(false);
                  setDomainError("");
                }}
                placeholder="example.com"
                autoComplete="url"
              />
              <small className="help-text">
                For example, example.com. Leave out “https://” and “www”. You
                will not be charged a domain registration fee.
              </small>
            </div>
          )}

          {domainData.domainType === "custom" && (
            <div className="custom-domain-section" data-tour="custom-domain">
              <div className="domain-how-to">
                <strong>Register a new domain in 3 steps</strong>
                <ol>
                  <li
                    className={
                      domainData.customDomain.length >= 3 ? "complete" : ""
                    }
                  >
                    Enter a name
                  </li>
                  <li
                    className={
                      domainExtensions.some(
                        (extension) =>
                          extension.extension === domainData.selectedExtension,
                      )
                        ? "complete"
                        : ""
                    }
                  >
                    Choose an ending
                  </li>
                  <li
                    className={
                      domainSettings.enable_availability_check
                        ? domainData.availability?.available === true
                          ? "complete"
                          : ""
                        : domainAvailabilityAcknowledged
                          ? "complete"
                          : ""
                    }
                  >
                    {domainSettings.enable_availability_check
                      ? "Check availability"
                      : "Confirm availability notice"}
                  </li>
                </ol>
              </div>
              <label className="domain-field-label" htmlFor="custom-domain">
                1. Enter the name you want
              </label>
              <p className="domain-field-help">
                Type the name only, without dots or an extension. For example,
                type <strong>myshop</strong> to make myshop.co.za.
              </p>
              <div className="domain-input-group">
                <div
                  className="domain-input-wrapper"
                  data-tour="custom-domain-name"
                >
                  <input
                    type="text"
                    id="custom-domain"
                    value={domainData.customDomain}
                    onChange={(e) => {
                      const value = e.target.value
                        .toLowerCase()
                        .replace(/[^a-z0-9-]/g, "");
                      setDomainData((prev) => ({
                        ...prev,
                        customDomain: value,
                        availability: null,
                        verificationStatus: null,
                      }));
                      setShowDomainCostNotice(false);
                      setDomainAvailabilityAcknowledged(false);
                      setDomainError("");
                    }}
                    placeholder="yourstore"
                    aria-label="Preferred domain name"
                  />
                  <select
                    value={domainData.selectedExtension}
                    onChange={(e) => {
                      setDomainData((prev) => ({
                        ...prev,
                        selectedExtension: e.target.value,
                        availability: null,
                        verificationStatus: null,
                      }));
                      setShowDomainCostNotice(false);
                      setDomainAvailabilityAcknowledged(false);
                    }}
                    className="extension-select"
                    aria-label="Choose a domain extension"
                  >
                    {domainExtensions.map((ext) => (
                      <option key={ext.extension} value={ext.extension}>
                        {ext.extension} {ext.popular ? "⭐" : ""} —{" "}
                        {isLitePackage()
                          ? `R${getDomainPriceWithTax(Number(ext.price || 0)).toFixed(2)}/yr`
                          : getDomainChargeBeforeTax(ext.extension) > 0
                            ? `R${getDomainPriceWithTax(getDomainChargeBeforeTax(ext.extension)).toFixed(2)}/yr upgrade`
                            : "Included"}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Only show verify/suggestions buttons if availability check is enabled */}
                {domainSettings.enable_availability_check && (
                  <>
                    <button
                      type="button"
                      className="verify-btn"
                      data-tour="availability-check"
                      onClick={handleVerifyDomain}
                      disabled={
                        domainLoading ||
                        !domainData.customDomain ||
                        domainData.customDomain.length < 3
                      }
                    >
                      {domainLoading
                        ? "Checking domain..."
                        : "3. Check availability"}
                    </button>
                    {domainData.customDomain &&
                      domainData.customDomain.length >= 3 &&
                      !isServiceOffline && (
                        <button
                          type="button"
                          className="verify-btn"
                          onClick={handleGetSuggestions}
                          style={{ background: "#6b7280" }}
                        >
                          Try name suggestions
                        </button>
                      )}
                  </>
                )}
              </div>

              {isChecking && (
                <div className="checking-status">
                  <span className="spinner-small"></span> Checking
                  availability...
                </div>
              )}

              {domainSettings.enable_availability_check &&
                domainData.availability && (
                  <div
                    className={`availability-status ${isServiceOffline ? "offline" : domainData.availability.available ? "available" : "unavailable"}`}
                  >
                    {isServiceOffline ? (
                      <>
                        <span>⚠️</span>
                        <div style={{ flex: 1 }}>
                          <strong>Domain Service Offline</strong>
                          <p className="error-message">
                            {domainData.availability.error ||
                              "Unable to check domain availability. Please try again later."}
                          </p>
                          <button
                            className="retry-btn"
                            onClick={handleVerifyDomain}
                            disabled={domainLoading}
                            style={{ marginTop: "8px" }}
                          >
                            {domainLoading ? "Checking..." : "Try Again"}
                          </button>
                        </div>
                      </>
                    ) : domainData.availability.available ? (
                      <>
                        <span>✅</span>
                        <strong>
                          {domainData.customDomain}
                          {domainData.selectedExtension}
                        </strong>{" "}
                        available!
                        <span className="domain-price">
                          {isLitePackage()
                            ? `R${getDomainPriceWithTax(getDomainChargeBeforeTax()).toFixed(2)}/yr`
                            : getDomainChargeBeforeTax() > 0
                              ? `R${getDomainPriceWithTax(getDomainChargeBeforeTax()).toFixed(2)}/yr upgrade`
                              : "Included with your package"}
                        </span>
                      </>
                    ) : (
                      <>
                        <span>❌</span>
                        <strong>
                          {domainData.customDomain}
                          {domainData.selectedExtension}
                        </strong>{" "}
                        taken
                        {domainData.suggestions &&
                          domainData.suggestions.length > 0 && (
                            <div className="suggestions">
                              <p>Suggestions:</p>
                              {domainData.suggestions
                                .slice(0, 5)
                                .map((s, i) => (
                                  <button
                                    key={i}
                                    className="suggestion-btn"
                                    onClick={() => {
                                      const cleanS = s.replace(/^www\./, "");
                                      const domainParts = cleanS.split(".");
                                      setDomainData((prev) => ({
                                        ...prev,
                                        customDomain: domainParts[0] || cleanS,
                                        availability: null,
                                        verificationStatus: null,
                                      }));
                                    }}
                                  >
                                    {s}
                                  </button>
                                ))}
                            </div>
                          )}
                      </>
                    )}
                  </div>
                )}

              {domainSettings.enable_availability_check &&
                domainData.verificationStatus && (
                  <div
                    className={`verification-status ${domainData.verificationStatus}`}
                  >
                    <span className="status-icon">
                      {domainData.verificationStatus === "available"
                        ? "✅"
                        : domainData.verificationStatus === "checking"
                          ? "⏳"
                          : domainData.verificationStatus === "unavailable"
                            ? "❌"
                            : domainData.verificationStatus === "failed"
                              ? "❌"
                              : "⏳"}
                    </span>
                    <span>
                      {domainData.verificationStatus === "available"
                        ? "Available. Any applicable registration charge is shown in your domain summary and invoice."
                        : domainData.verificationStatus === "checking"
                          ? "Checking availability..."
                          : domainData.verificationStatus === "unavailable"
                            ? "Domain is taken"
                            : domainData.verificationStatus === "failed"
                              ? "Failed. Try again."
                              : "Check availability"}
                    </span>
                    {domainData.verificationStatus === "unavailable" && (
                      <button
                        className="retry-btn"
                        onClick={handleVerifyDomain}
                        disabled={domainLoading}
                      >
                        Check Again
                      </button>
                    )}
                  </div>
                )}

              {!domainSettings.enable_availability_check && (
                <label
                  className="domain-unchecked-acknowledgement"
                  data-tour="availability-acknowledgement"
                >
                  <input
                    type="checkbox"
                    checked={domainAvailabilityAcknowledged}
                    onChange={(event) => {
                      setDomainAvailabilityAcknowledged(event.target.checked);
                      setDomainError("");
                    }}
                  />
                  <span>
                    <strong>Continue without an availability check</strong>
                    <small>
                      Availability cannot be checked right now. We will verify
                      this domain before registration.
                    </small>
                  </span>
                </label>
              )}
              <div
                className="domain-selection-summary"
                aria-live="polite"
                data-tour="domain-total"
              >
                <span>Your chosen domain</span>
                <strong>
                  {domainData.customDomain || "yourname"}
                  {domainData.selectedExtension || ".co.za"}
                </strong>
                <span>
                  {isLitePackage()
                    ? `Domain price: R${getDomainPriceWithTax(getDomainChargeBeforeTax()).toFixed(2)} / year`
                    : getDomainChargeBeforeTax() > 0
                      ? `Included allowance: .co.za up to R${getDomainPriceWithTax(getIncludedDomainAllowance()).toFixed(2)} / year. Your upgrade: R${getDomainPriceWithTax(getDomainChargeBeforeTax()).toFixed(2)} / year`
                      : `Included in your package. Your .co.za allowance is up to R${getDomainPriceWithTax(getIncludedDomainAllowance()).toFixed(2)} / year.`}
                </span>
              </div>
            </div>
          )}
        </div>
      </motion.div>
    );
  };

  // ============ STEP 4: Store Design ============
  const renderStep4 = () => (
    <motion.div
      key="step4"
      custom={direction}
      variants={slideVariants}
      initial="enter"
      animate="center"
      exit="exit"
      className="step-content"
      data-tour="step-content"
    >
      <div className="step-header-compact">
        <h2>Store Design</h2>
        <p>Customize the look and feel of your store</p>
      </div>

      <div className="step-form">
        <div className="form-section">
          <h4>Choose a Template</h4>
          <div className="template-grid" data-tour="design-template">
            {templates.map((template) => (
              <div
                key={template.id}
                className={`template-card ${designData.template === template.id ? "selected" : ""}`}
                onClick={() =>
                  setDesignData((prev) => ({ ...prev, template: template.id }))
                }
              >
                <div className="template-icon">{template.icon}</div>
                <h5>{template.name}</h5>
                <p>{template.description}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="form-row" data-tour="design-colors">
          <div className="form-group">
            <label>Primary Color</label>
            <div className="color-picker">
              {colors.map((color) => (
                <button
                  key={color}
                  className={`color-option ${designData.primaryColor === color ? "selected" : ""}`}
                  style={{ background: color }}
                  onClick={() =>
                    setDesignData((prev) => ({ ...prev, primaryColor: color }))
                  }
                />
              ))}
            </div>
          </div>
          <div className="form-group">
            <label>Secondary Color</label>
            <div className="color-picker">
              {colors.map((color) => (
                <button
                  key={color}
                  className={`color-option ${designData.secondaryColor === color ? "selected" : ""}`}
                  style={{ background: color }}
                  onClick={() =>
                    setDesignData((prev) => ({
                      ...prev,
                      secondaryColor: color,
                    }))
                  }
                />
              ))}
            </div>
          </div>
        </div>

        <div className="form-group">
          <label>Font</label>
          <select
            value={designData.font}
            onChange={(e) =>
              setDesignData((prev) => ({ ...prev, font: e.target.value }))
            }
          >
            {fonts.map((font) => (
              <option key={font} value={font}>
                {font}
              </option>
            ))}
          </select>
        </div>

        <div className="form-row" data-tour="design-assets">
          <div className="form-group">
            <label>Store Logo</label>
            <input
              type="file"
              accept="image/*"
              onChange={(e) =>
                setDesignData((prev) => ({ ...prev, logo: e.target.files[0] }))
              }
            />
            <small className="help-text">Upload your store logo</small>
          </div>
          <div className="form-group">
            <label>Homepage Banner</label>
            <input
              type="file"
              accept="image/*"
              onChange={(e) =>
                setDesignData((prev) => ({
                  ...prev,
                  banner: e.target.files[0],
                }))
              }
            />
            <small className="help-text">Upload a hero banner image</small>
          </div>
        </div>
      </div>
    </motion.div>
  );

  // ============ STEP 5: Products ============
  const renderStep5 = () => (
    <motion.div
      key="step5"
      custom={direction}
      variants={slideVariants}
      initial="enter"
      animate="center"
      exit="exit"
      className="step-content"
      data-tour="step-content"
    >
      <div className="step-header-compact">
        <h2>Products</h2>
        <p>Add your products to start selling</p>
      </div>

      <div className="step-form">
        <p className="hint-text">
          You can skip this step and add products later from your dashboard.
        </p>

        <div className="products-header" data-tour="products-header">
          <span className="product-count">
            {products.length} product{products.length !== 1 ? "s" : ""} added
          </span>
          <button
            className="btn-secondary"
            onClick={() => setShowAddForm(!showAddForm)}
          >
            {showAddForm ? "Cancel" : "+ Add Product"}
          </button>
        </div>

        {showAddForm && (
          <div className="product-form">
            {Object.keys(productErrors).length > 0 && (
              <div className="form-errors-summary">
                <ul>
                  {Object.values(productErrors).map((error, index) => (
                    <li key={index}>• {error}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="form-row">
              <div className="form-group">
                <label>Product Name *</label>
                <input
                  type="text"
                  name="name"
                  value={currentProduct.name}
                  onChange={handleProductChange}
                  placeholder="Enter product name"
                  className={productErrors.name ? "error" : ""}
                />
                {productErrors.name && (
                  <span className="error-text">{productErrors.name}</span>
                )}
              </div>
              <div className="form-group">
                <label>Price *</label>
                <input
                  type="number"
                  name="price"
                  value={currentProduct.price}
                  onChange={handleProductChange}
                  placeholder="0.00"
                  step="0.01"
                  min="0"
                  className={productErrors.price ? "error" : ""}
                />
                {productErrors.price && (
                  <span className="error-text">{productErrors.price}</span>
                )}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Stock Quantity</label>
                <input
                  type="number"
                  name="stock"
                  value={currentProduct.stock}
                  onChange={handleProductChange}
                  placeholder="0"
                  min="0"
                />
              </div>
              <div className="form-group">
                <label>Category</label>
                <select
                  name="category"
                  value={currentProduct.category}
                  onChange={handleProductChange}
                >
                  <option value="">Select category</option>
                  <option value="clothing">Clothing & Fashion</option>
                  <option value="electronics">Electronics</option>
                  <option value="food">Food & Beverage</option>
                  <option value="home">Home & Garden</option>
                  <option value="beauty">Health & Beauty</option>
                  <option value="sports">Sports & Outdoors</option>
                  <option value="toys">Toys & Games</option>
                  <option value="books">Books & Media</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>

            <div className="form-group">
              <label>Description</label>
              <textarea
                name="description"
                value={currentProduct.description}
                onChange={handleProductChange}
                rows="2"
                placeholder="Enter product description"
              />
            </div>

            <div className="form-group">
              <label>Product Image</label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) =>
                  setCurrentProduct((prev) => ({
                    ...prev,
                    image: e.target.files[0],
                  }))
                }
              />
              <small className="help-text">
                Upload a product image (PNG, JPG)
              </small>
            </div>

            <button
              className="btn-primary add-product-btn"
              onClick={addProduct}
            >
              + Add Product
            </button>
          </div>
        )}

        {products.length > 0 && (
          <div className="products-list">
            {products.map((product) => (
              <div key={product.id} className="product-item">
                <div className="product-info">
                  <span className="product-name">{product.name}</span>
                  <span className="product-price">
                    R{parseFloat(product.price).toFixed(2)}
                  </span>
                  <span className="product-stock">
                    Stock: {product.stock || 0}
                  </span>
                </div>
                <button
                  className="btn-danger remove-btn"
                  onClick={() => removeProduct(product.id)}
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </motion.div>
  );

  // ============ STEP 6: Agreement ============
  const renderStep6 = () => {
    const contractDocument = getRenderedContract({
      agreementNumber,
      acceptedAt: new Date().toISOString(),
    });

    return (
      <motion.div
        key="step6"
        custom={direction}
        variants={slideVariants}
        initial="enter"
        animate="center"
        exit="exit"
        className="step-content"
        data-tour="step-content"
      >
        <div className="step-header-compact">
          <h2>Service Agreement</h2>
          <p>Review and accept the service agreement</p>
        </div>

        <div className="step-form">
          {agreementError && (
            <div className="validation-error-message">
              <i className="fas fa-exclamation-circle"></i>
              {agreementError}
            </div>
          )}

          <div className="agreement-container" data-tour="agreement-document">
            <div className="agreement-content">
              <div className="contract-document-brand">
                {brandLogo ? (
                  <img src={brandLogo} alt="Toora Flex" />
                ) : (
                  <span className="invoice-logo">TF</span>
                )}
                <span>Online Store Contract</span>
              </div>
              <h3>{contractDocument.title}</h3>
              <pre className="contract-document-text">
                {contractDocument.content}
              </pre>
            </div>

            <div className="agreement-actions">
              <button
                type="button"
                className="btn-primary download-btn"
                onClick={() => downloadDocument("agreement")}
              >
                <i className="fas fa-download"></i> Download Contract (PDF)
              </button>
              <span>Choose “Save as PDF” in the print dialog.</span>
            </div>

            <div
              className="agreement-acceptance"
              data-tour="agreement-acceptance"
            >
              <label className="checkbox-label agreement-checkbox">
                <input
                  type="checkbox"
                  checked={agreementAccepted}
                  onChange={(event) =>
                    handleAgreementAcceptanceChange(event.target.checked)
                  }
                />
                <span>
                  <strong>
                    I have read and agree to the terms and conditions
                  </strong>
                  <small>
                    By checking this box, you agree to our{" "}
                    <button
                      type="button"
                      className="legal-link-button"
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        setLegalModal("terms");
                      }}
                    >
                      Terms of Service
                    </button>{" "}
                    and{" "}
                    <button
                      type="button"
                      className="legal-link-button"
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        setLegalModal("privacy");
                      }}
                    >
                      Privacy Policy
                    </button>
                  </small>
                </span>
              </label>
              {agreementAccepted && (
                <div className="contract-client-name">
                  <p>
                    Confirm the first and last name to appear on your contract.
                    You can edit either field before continuing.
                  </p>
                  <div className="contract-client-name-fields">
                    <label>
                      First name
                      <input
                        type="text"
                        autoComplete="given-name"
                        value={legalFirstName}
                        onChange={(event) => {
                          setLegalFirstName(event.target.value);
                          if (agreementError) setAgreementError("");
                        }}
                        required
                      />
                    </label>
                    <label>
                      Surname
                      <input
                        type="text"
                        autoComplete="family-name"
                        value={legalSurname}
                        onChange={(event) => {
                          setLegalSurname(event.target.value);
                          if (agreementError) setAgreementError("");
                        }}
                        required
                      />
                    </label>
                  </div>
                  <p className="contract-client-name-preview">
                    Name on contract: <strong>{getLegalFullName()}</strong>
                  </p>
                </div>
              )}
            </div>
            {legalModal && (
              <div
                className="legal-modal-backdrop"
                role="presentation"
                onMouseDown={(event) => {
                  if (event.target === event.currentTarget) setLegalModal("");
                }}
              >
                <section
                  className="legal-modal"
                  role="dialog"
                  aria-modal="true"
                  aria-labelledby="legal-modal-title"
                >
                  <header className="legal-modal-header">
                    <h3 id="legal-modal-title">
                      {legalModal === "terms"
                        ? "Online Store Terms of Service"
                        : "Privacy Policy"}
                    </h3>
                    <button
                      type="button"
                      className="legal-modal-close"
                      aria-label="Close"
                      onClick={() => setLegalModal("")}
                    >
                      <i className="fas fa-times" aria-hidden="true" />
                    </button>
                  </header>
                  <div className="legal-modal-body">
                    {legalModal === "terms" ? (
                      <>
                        <p>
                          These service terms are set out in the Online Store
                          Contract below. Please review the full agreement
                          before accepting it.
                        </p>
                        <h4>{contractDocument.title}</h4>
                        <pre className="contract-document-text">
                          {contractDocument.content}
                        </pre>
                      </>
                    ) : (
                      <Privacy />
                    )}
                  </div>
                </section>
              </div>
            )}
          </div>
        </div>
      </motion.div>
    );
  };

  // ============ STEP 7: Invoice & Payment ============
  const renderStep7 = () => {
    const today = new Date();
    const domainBaseCost =
      !isTrial && domainData.domainType === "custom" ? getDomainBasePrice() : 0;
    const domainChargeBeforeTax = getDomainChargeBeforeTax();
    const domainTax = getDomainTax(domainChargeBeforeTax);
    const domainTotal = roundMoney(domainChargeBeforeTax + domainTax);
    const domainListedTotal = getDomainPriceWithTax(domainBaseCost);
    const domainAllowanceCredit = roundMoney(domainListedTotal - domainTotal);
    const packageCharge = isTrial
      ? 0
      : Number(selectedPackageDetails?.Price || 0);
    const subtotal = roundMoney(packageCharge + domainTotal);
    const totalUpfront = subtotal;
    const totalDue = totalUpfront;
    const billingPeriod = selectedPackageDetails?.BillingPeriod || "month";
    const selectedPaymentMethodDetails = paymentMethods.find(
      (method) =>
        String(method.code).toLowerCase() ===
        String(selectedPaymentMethod).toLowerCase(),
    );

    return (
      <motion.div
        key="step7"
        custom={direction}
        variants={slideVariants}
        initial="enter"
        animate="center"
        exit="exit"
        className="step-content"
        data-tour="step-content"
      >
        <div className="step-header-compact">
          <h2>{isTrial ? "Review Your Trial" : "Invoice & Payment"}</h2>
          <p>
            {isTrial
              ? "No payment is due during the trial. Custom domains are not included."
              : "Choose a payment option to submit your store request."}
          </p>
        </div>

        {success && (
          <div className="request-alert request-alert-success">
            <i className="fas fa-check-circle"></i>
            <span>{success}</span>
          </div>
        )}

        {error && (
          <div className="request-alert request-alert-error">
            <i className="fas fa-exclamation-circle"></i>
            <span>{error}</span>
          </div>
        )}

        <div className="invoice-container" data-tour="invoice-summary">
          <div className="invoice-header">
            <div className="invoice-brand">
              {brandLogo ? (
                <img
                  className="invoice-brand-logo"
                  src={brandLogo}
                  alt="Toora Flex"
                />
              ) : (
                <div className="invoice-logo">TF</div>
              )}
              <div>
                <h3>Toora Flex</h3>
                <small>Online Store Services</small>
              </div>
            </div>
            <div className="invoice-details">
              <div className="invoice-number">
                <span>Invoice #</span>
                <strong>{invoiceNumber}</strong>
              </div>
              <div className="invoice-date">
                <span>Date</span>
                <strong>
                  {today.toLocaleDateString("en-ZA", {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                  })}
                </strong>
              </div>
              <div className="invoice-due">
                <span>Due Date</span>
                <strong>Upon Signature</strong>
              </div>
            </div>
          </div>

          <div className="invoice-bill-to">
            <div className="bill-to-section">
              <h4>Bill To:</h4>
              <p>
                <strong>{formData.businessName || "Client"}</strong>
              </p>
              <p>Client: {getLegalFullName()}</p>
              <p>{formData.email || "N/A"}</p>
              <p>{formData.phone || "N/A"}</p>
            </div>
            <div className="bill-to-section">
              <h4>Issued By:</h4>
              <p>
                <strong>Toora Flex (Pty) Ltd</strong>
              </p>
              <p>Reg No: 2026/036648/07</p>
              <p>367 Helen Joseph Street, Pretoria Central</p>
            </div>
          </div>

          <div className="invoice-billing-note">
            <strong>
              Selected package: {selectedPackageDetails?.Name || "Package not selected"}
              {selectedPackageDetails?.Tier
                ? ` (${selectedPackageDetails.Tier})`
                : ""}
            </strong>
            <p>
              {isTrial
                ? "Trial package: no package or domain charge is due during the trial."
                : `Package charge: R${packageCharge.toFixed(2)} per ${billingPeriod}, included in the total due now.`}
            </p>
          </div>
          {!isTrial && domainData.domainType === "custom" && (
            <div className="availability-disabled-notice" role="note">
              <i className="fas fa-info-circle" aria-hidden="true" />
              <span>
                GoDaddy availability and pricing may change before registration.
                The final availability and cost will be confirmed at
                registration, and any price change will be confirmed with you
                before proceeding.
              </span>
            </div>
          )}

          <table className="invoice-table" data-tour="invoice-table">
            <thead>
              <tr>
                <th>Description</th>
                <th>Qty</th>
                <th>Unit Price</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  {isTrial
                    ? `Trial package — ${selectedPackageDetails?.Name || "Selected package"}`
                    : `Selected package — ${selectedPackageDetails?.Name || "Package not selected"}`}
                </td>
                <td>1</td>
                <td>R{packageCharge.toFixed(2)}</td>
                <td>R{packageCharge.toFixed(2)}</td>
              </tr>
              <tr>
                <td>
                  {isTrial
                    ? `Trial subdomain (${domainData.subdomain}.tooraflex.co.za)`
                    : domainData.domainType === "existing"
                      ? `Existing domain (${domainData.existingDomain})`
                      : `Annual domain registration (${domainData.customDomain}${domainData.selectedExtension})`}
                </td>
                <td>1</td>
                <td>R{domainListedTotal.toFixed(2)}</td>
                <td>R{domainListedTotal.toFixed(2)}</td>
              </tr>
              {!isTrial &&
                domainData.domainType === "custom" &&
                !isLitePackage() && (
                  <tr className="invoice-domain-allowance">
                    <td>
                      Included domain allowance ({domainData.customDomain}
                      {domainData.selectedExtension}) — up to the .co.za price
                    </td>
                    <td>1</td>
                    <td>-R{domainAllowanceCredit.toFixed(2)}</td>
                    <td>-R{domainAllowanceCredit.toFixed(2)}</td>
                  </tr>
                )}
              <tr className="invoice-subtotal">
                <td colSpan="3">
                  <strong>Subtotal</strong>
                </td>
                <td>
                  <strong>R{subtotal.toFixed(2)}</strong>
                </td>
              </tr>
              <tr className="invoice-total">
                <td colSpan="3">
                  <strong>TOTAL DUE</strong>
                </td>
                <td>
                  <strong>R{totalDue.toFixed(2)}</strong>
                </td>
              </tr>
            </tbody>
          </table>

          {!isTrial && (
            <div className="invoice-billing-note">
              {selectedPackageDetails?.SetupFee > 0 && (
                <p>
                  Package setup fee is not charged.
                </p>
              )}
              {isLitePackage() ? (
                <p>
                  Lite does not include a free custom domain. The selected
                  extension is charged at its full listed annual price.
                </p>
              ) : (
                <p>
                  Your package includes a free annual domain allowance equal to
                  the current .co.za price (R
                  {getDomainPriceWithTax(getIncludedDomainAllowance()).toFixed(
                    2,
                  )}
                  ). If the chosen extension costs more, only the difference is
                  charged; a cheaper extension is free.
                </p>
              )}
            </div>
          )}

          <div className="invoice-payment-details">
            <h4>{isTrial ? "Trial terms" : "Payment option"}</h4>
            {!isTrial && !editRequestId && getCurrentPaymentDue() > 0 && (
              <div className="payment-requirement-note" role="note">
                <strong>Payment is required before store setup can begin.</strong>
                <p>
                  Choose Pay now to complete payment now. Or select Submit
                  &amp; pay later to submit your request and return to its
                  status page when you are ready to pay. Saving as draft only
                  saves your progress; it does not submit the request.
                </p>
              </div>
            )}
            {isTrial ? (
              <p>
                Your trial length is set by the administrator. You can select a
                paid plan or add a custom domain after the trial.
              </p>
            ) : paymentResult ? (
              <div>
                <p>
                  <strong>Reference:</strong> {paymentResult.reference}
                </p>
                <p>
                  <strong>Amount:</strong> {paymentResult.currency}{" "}
                  {Number(paymentResult.amount).toFixed(2)}
                </p>
                <p>
                  <strong>Status:</strong> Payment awaiting verification
                </p>
                {(paymentResult.instructions ||
                  (selectedPaymentMethodDetails &&
                    (selectedPaymentMethodDetails.code === "eft" ||
                      !selectedPaymentMethodDetails.online))) && (
                  <div className="bank-details-card">
                    <div className="bank-details-heading">
                      <span className="bank-details-icon" aria-hidden="true">
                        <i className="fas fa-university"></i>
                      </span>
                      <div>
                        <h5>
                          {selectedPaymentMethodDetails?.code === "eft" ||
                          String(paymentResult.method)
                            .toLowerCase()
                            .includes("eft")
                            ? "Bank transfer details"
                            : `${paymentResult.method} instructions`}
                        </h5>
                        <p>
                          {paymentResult.instructions ||
                          selectedPaymentMethodDetails?.instructions
                            ? "Use these details when making your transfer."
                            : "Bank details have not been configured yet."}
                        </p>
                      </div>
                    </div>
                    <EFTDetailList
                      details={selectedPaymentMethodDetails?.eftDetails}
                      instructions={
                        paymentResult.instructions ||
                        selectedPaymentMethodDetails?.instructions
                      }
                      fallback="The administrator needs to add the bank name, account holder, account number and branch code in Payment Settings before an EFT transfer can be made."
                    />
                  </div>
                )}
                {gatewayCheckout && (
                  <form
                    ref={gatewayFormRef}
                    method="post"
                    action={gatewayCheckout.action}
                    className="gateway-checkout-form"
                    aria-hidden="true"
                  >
                    {Object.entries(gatewayCheckout.fields).map(
                      ([name, value]) => (
                        <input
                          key={name}
                          type="hidden"
                          name={name}
                          value={value}
                        />
                      ),
                    )}
                  </form>
                )}
                {redirectingToGateway && (
                  <p className="payment-gateway-redirect" role="status">
                    <span className="spinner-small" aria-hidden="true" />
                    Redirecting to {paymentResult.method}...
                  </p>
                )}
                <p className="payment-note">
                  Your client status page will show when an administrator
                  confirms receipt of your payment.
                </p>
              </div>
            ) : (
              <div className="form-group">
                {paymentMethods.length > 0 ? (
                  <>
                    <fieldset
                      className="payment-method-selector"
                      data-tour="payment-methods"
                    >
                      <legend>Select how you want to pay</legend>
                      <div className="payment-methods">
                        {paymentMethods.map((method) => (
                          <label
                            className={`payment-option ${
                              selectedPaymentMethod === method.code
                                ? "selected"
                                : ""
                            }`}
                            key={method.code}
                          >
                            <input
                              type="radio"
                              name="paymentMethod"
                              value={method.code}
                              checked={selectedPaymentMethod === method.code}
                              onChange={() => {
                                setSelectedPaymentMethod(method.code);
                                setPaymentResult(null);
                                setGatewayCheckout(null);
                              }}
                            />
                            {method.paymentImage && (
                              <img
                                className="payment-option-image"
                                src={method.paymentImage}
                                alt=""
                              />
                            )}
                            <span className="payment-info">
                              <strong>{method.displayName}</strong>
                              <small>
                                {method.online
                                  ? "Online payment"
                                  : "Manual payment"}
                              </small>
                            </span>
                          </label>
                        ))}
                      </div>
                    </fieldset>
                    {selectedPaymentMethodDetails &&
                      (selectedPaymentMethodDetails.code === "eft" ||
                        !selectedPaymentMethodDetails.online) && (
                        <div className="bank-details-card">
                          <div className="bank-details-heading">
                            <span
                              className="bank-details-icon"
                              aria-hidden="true"
                            >
                              <i className="fas fa-university"></i>
                            </span>
                            <div>
                              <h5>
                                {selectedPaymentMethodDetails.code === "eft"
                                  ? "Bank transfer details"
                                  : `${selectedPaymentMethodDetails.displayName} details`}
                              </h5>
                              <p>
                                {selectedPaymentMethodDetails.instructions
                                  ? "Use these details when making your transfer."
                                  : "Bank details have not been added yet."}
                              </p>
                            </div>
                          </div>
                          <EFTDetailList
                            details={selectedPaymentMethodDetails.eftDetails}
                            instructions={
                              selectedPaymentMethodDetails.instructions
                            }
                            fallback="The administrator needs to add the bank details in Admin > Payments before an EFT transfer can be made."
                          />
                        </div>
                      )}
                  </>
                ) : (
                  <p className="payment-note">
                    Payment options are not available yet. Please contact
                    support.
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Pay now / Pay later / Save as draft live in the sticky bottom bar */}
          <div className="invoice-actions">
            <button
              type="button"
              className="btn-primary download-btn"
              onClick={() => downloadDocument("invoice")}
            >
              <i className="fas fa-download"></i> Download Invoice (PDF)
            </button>
          </div>
        </div>
      </motion.div>
    );
  };

  // ============ MAIN RENDER ============
  if (loading || stepsLoading || !draftInitialized) {
    return (
      <div
        className="request-store-page"
        onChangeCapture={() => setDraftTouched(true)}
        onClickCapture={() => setDraftTouched(true)}
      >
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading available packages...</p>
        </div>
      </div>
    );
  }

  if (editRequestId && editRequestError) {
    return (
      <div className="request-store-page">
        <div className="request-alert request-alert-error" role="alert">
          <i className="fas fa-exclamation-circle" aria-hidden="true" />
          <span>{editRequestError}</span>
        </div>
        <button
          type="button"
          className="btn-secondary"
          onClick={() => navigate(`/request-status/${editRequestId}`)}
        >
          Back to request
        </button>
      </div>
    );
  }

  const totalSteps = getTotalSteps();

  if (totalSteps === 0) {
    return (
      <div
        className="request-store-page"
        onChangeCapture={() => setDraftTouched(true)}
        onClickCapture={() => setDraftTouched(true)}
      >
        <div className="loading-container">
          <p>No onboarding steps are currently configured.</p>
          <p>Please contact the administrator.</p>
        </div>
      </div>
    );
  }

  const currentStepId = getStepIdByIndex(currentStep);
  const guideStepCount = guideSteps.length;
  const guidePanelWidth = Math.min(360, window.innerWidth - 24);
  const guidePanelPosition = guideTargetRect
    ? (() => {
        const belowTargetTop = guideTargetRect.bottom + 14;
        const top =
          belowTargetTop + 300 < window.innerHeight
            ? belowTargetTop
            : Math.max(12, guideTargetRect.top - 320);
        const left = Math.max(
          12,
          Math.min(
            guideTargetRect.left,
            window.innerWidth - guidePanelWidth - 12,
          ),
        );
        return { top, left };
      })()
    : undefined;
  const isNextDisabled =
    (currentStepId === "package" && !selectedPackage) ||
    (currentStepId === "business" && !isBusinessStepValid()) ||
    (currentStepId === "domain" &&
      (isTrial
        ? !domainData.subdomain || domainData.subdomain.length < 3
        : domainData.domainType === "existing"
          ? !isValidExistingDomain(domainData.existingDomain)
          : domainExtensions.length === 0 ||
            !domainData.customDomain ||
            domainData.customDomain.length < 3 ||
            !domainExtensions.some(
              (extension) =>
                extension.extension === domainData.selectedExtension,
            ) ||
            (domainSettings.enable_availability_check
              ? domainData.availability?.available !== true || isChecking
              : !domainAvailabilityAcknowledged))) ||
    (currentStepId === "agreement" && !agreementAccepted) ||
    currentStep >= totalSteps;

  return (
    <div
      className="request-store-page"
      onChangeCapture={() => setDraftTouched(true)}
      onClickCapture={() => setDraftTouched(true)}
    >
      {!editRequestId && !domainPriceNoticeAcknowledged && (
        <DomainPriceNoticeModal
          onAcknowledge={() => setDomainPriceNoticeAcknowledged(true)}
          onClose={() => navigate("/dashboard", { replace: true })}
        />
      )}
      {(submitting || redirectingToGateway) && (
        <div
          className="payment-loading-overlay"
          role="status"
          aria-live="polite"
        >
          <span className="payment-loading-spinner" aria-hidden="true" />
          <p>
            {redirectingToGateway
              ? `Redirecting to ${paymentResult?.method || "the payment gateway"}...`
              : isTrial
                ? "Starting your trial..."
                : "Preparing your payment..."}
          </p>
        </div>
      )}
      <div className="request-store-header-compact">
        <h1>{editRequestId ? "Edit Store Request" : "Start Your Store"}</h1>
        <p>
          {editRequestId
            ? "Update your request details. Changes will be sent for review."
            : currentStep === 0
            ? "Answer a few questions so our team can understand your store request"
            : "Choose a package and set up your store in minutes"}
        </p>
        {currentStep === 0 && draftStatus && (
          <p className="request-draft-feedback" role="status">
            {draftStatus}
          </p>
        )}
      </div>
      {currentStep > 0 && renderStepIndicator()}

      {error && currentStep !== totalSteps && (
        <div className="request-alert request-alert-error">
          <i className="fas fa-exclamation-circle"></i>
          <span>{error}</span>
        </div>
      )}

      <div className="step-content-wrapper">
        <AnimatePresence mode="wait" custom={direction}>
          {renderStepContent()}
        </AnimatePresence>

        {currentStep > 0 && (
          <div className="step-navigation-compact">
            {(currentStep > 1 || assessmentComplete) && (
              <button
                type="button"
                className="btn-nav back"
                onClick={handlePreviousStep}
              >
                <i className="fas fa-arrow-left"></i> Back
              </button>
            )}
            {draftStatus && (
              <span className="request-draft-feedback" role="status">
                {draftStatus}
              </span>
            )}
            <div className="nav-spacer" />
            {currentStep >= totalSteps ? (
              <div
                className={`request-next-actions request-final-actions ${
                  editRequestId ? "is-editing-request" : ""
                }`}
              >
                {!editRequestId && !isTrial && getCurrentPaymentDue() > 0 && (
                  <button
                    type="button"
                    className="btn-secondary pay-later-btn"
                    data-tour="pay-later"
                    onClick={(event) => handleSubmit(event, true)}
                    disabled={submitting || Boolean(paymentResult)}
                    aria-label="Submit store request and pay later"
                  >
                    Submit &amp; pay later
                  </button>
                )}
                <button
                  type="button"
                  className="btn-primary submit-btn-final"
                  data-tour="submit-request"
                  onClick={handleSubmit}
                  disabled={
                    submitting ||
                    (!editRequestId &&
                      !isTrial &&
                      getCurrentPaymentDue() > 0 &&
                      (!selectedPaymentMethod || Boolean(paymentResult))) ||
                    Boolean(gatewayCheckout)
                  }
                >
                  {submitting ? (
                    <>
                      <span className="spinner-small"></span>{" "}
                      {isTrial ? "Submitting..." : "Preparing payment..."}
                    </>
                  ) : redirectingToGateway ? (
                    <>
                      <span className="spinner-small"></span> Redirecting...
                    </>
                  ) : isTrial ? (
                    editRequestId ? "Save changes" : "Start Free Trial"
                  ) : editRequestId ? (
                    "Save changes"
                  ) : paymentResult ? (
                    "Payment option selected"
                  ) : (
                    "Pay now"
                  )}
                </button>
                {editRequestId && !isTrial && getCurrentPaymentDue() > 0 && (
                  <button
                    type="button"
                    className="btn-primary edit-pay-now-btn"
                    onClick={(event) => handleSubmit(event, false, true)}
                    disabled={
                      submitting ||
                      !selectedPaymentMethod ||
                      Boolean(paymentResult) ||
                      Boolean(gatewayCheckout)
                    }
                  >
                    {submitting ? "Preparing payment..." : "Pay now"}
                  </button>
                )}
              </div>
            ) : (
              <div className="request-next-actions">
                <button
                  type="button"
                  className="btn-nav next"
                  data-tour="next-button"
                  onClick={handleNextStep}
                  disabled={isNextDisabled}
                >
                  Next <i className="fas fa-arrow-right"></i>
                </button>
                {renderSaveDraftButton()}
              </div>
            )}
          </div>
        )}
      </div>

      <AnimatePresence>
        {showFloatingButtons && (
          <motion.div
            className="floating-scroll-buttons"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.2 }}
          >
            <button className="floating-btn scroll-top" onClick={scrollToTop}>
              <i className="fas fa-arrow-up"></i>
            </button>
            <button
              className="floating-btn scroll-bottom"
              onClick={scrollToBottom}
            >
              <i className="fas fa-arrow-down"></i>
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isGuideOpen && (
          <motion.aside
            id="onboarding-step-guide"
            className="onboarding-guide-panel"
            aria-label="Step-by-step onboarding guide"
            aria-live="polite"
            style={guidePanelPosition}
            initial={{ opacity: 0, y: 12, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.98 }}
            transition={{ duration: 0.16 }}
          >
            <div className="onboarding-guide-header">
              <div className="onboarding-guide-heading">
                <span className="onboarding-guide-icon" aria-hidden="true">
                  <i className="fas fa-compass" />
                </span>
                <div>
                  <strong>{activeGuideStep?.title}</strong>
                  <small>
                    Guide {guideStepIndex + 1} of {guideStepCount}
                  </small>
                </div>
              </div>
              <button
                type="button"
                className="onboarding-guide-close"
                aria-label="Close step-by-step guide"
                onClick={() => setIsGuideOpen(false)}
              >
                <i className="fas fa-times" />
              </button>
            </div>
            <div className="onboarding-guide-content">
              <p>{activeGuideStep?.description}</p>
              <div className="onboarding-guide-actions">
                <button
                  type="button"
                  className="onboarding-guide-back"
                  onClick={() =>
                    setGuideStepIndex((index) => Math.max(0, index - 1))
                  }
                  disabled={guideStepIndex === 0}
                >
                  Back
                </button>
                {guideStepIndex < guideStepCount - 1 ? (
                  <button
                    type="button"
                    className="onboarding-guide-next"
                    onClick={() => setGuideStepIndex((index) => index + 1)}
                  >
                    Next tip <i className="fas fa-arrow-right" />
                  </button>
                ) : (
                  <button
                    type="button"
                    className="onboarding-guide-next"
                    onClick={() => setIsGuideOpen(false)}
                  >
                    Finish guide <i className="fas fa-check" />
                  </button>
                )}
              </div>
            </div>
          </motion.aside>
        )}
      </AnimatePresence>

      <button
        type="button"
        className={`onboarding-guide-toggle ${isGuideOpen ? "is-open" : ""}`}
        aria-label={
          isGuideOpen ? "Close step-by-step guide" : "Open step-by-step guide"
        }
        title={isGuideOpen ? "Close guide" : "Need help?"}
        aria-expanded={isGuideOpen}
        aria-controls="onboarding-step-guide"
        onClick={() => {
          if (!isGuideOpen) setGuideStepIndex(0);
          setIsGuideOpen((isOpen) => !isOpen);
        }}
      >
        <i
          className={`fas ${isGuideOpen ? "fa-times" : "fa-circle-question"}`}
        />
      </button>
    </div>
  );
};

export default RequestStore;
